import { i18n } from '../i18n.js';
/**
 * StoryFeed — turn-paged "story squares" battle log.
 *
 * Replaces the old always-on story rail (UI.Story) with one compact left-edge
 * column for the selected turn. Player and opponent beats share the column,
 * newest first and side-coloured, while the turn's manga recap sits above them.
 * Story text arrives per-play over `storyUpdate` (which already carries
 * playerId / turn / cardInstanceId / card identity); eligible manga recaps
 * arrive asynchronously over `mangaGenerationStarted` / `storyImageUpdate`.
 *
 * Layers in this module:
 *   - the feed STATE machine (entries + turn-keyed manga records)
 *   - the turn-column renderer (newest-first, capped, side-coloured)
 *   - the finite reveal queue: card-backed plays and completed manga expand in
 *     arrival order; hover pins; activation/Escape dismisses the transient
 *     queue; pending manga stays confined to its compact disabled tile until
 *     generation completes
 *   - desktop hover, touch tap, or keyboard activation reopens a settled beat;
 *     spell-float-announced opponent casts fill silently
 *   - the accessible manga zoom pager
 *   - the character-name highlight color engine (ported from UI.Story)
 *
 * Contracts: tests/unit/ui/storyFeed.{state,render,toast,history,turnColumns}.test.js
 * and tests/unit/styles/storyFeedStyles.test.js.
 */
import { CardbackArt } from './cardback-art.js';
import { CardDrag } from './card-drag.js';
import { CardHover } from './card-hover.js';
import { Cards } from './cards.js';
import { ImageZoom } from './image-zoom.js';
import { Mulligan } from './mulligan.js';
import { OrientationGuard } from './orientation-guard.js';
import { Platform } from './platform.js';
import { SoundEffects } from './sound-effects.js';
import { Soundtrack } from './soundtrack.js';
import { TouchPreview } from './touch-preview.js';
import { Tutorial } from './tutorial.js';
import { GameState } from '../game-state.js';
let StoryFeed = {};

(function () {
  const ENTRY_CAP = 400;
  const PENDING_GRACE_MS = 5000;
  const LANE_TILE_CAP = 30;
  // Kill-log timing: a played beat auto-expands then fades on its own, long
  // enough to read, so it never sits permanently over the board.
  /* READ IT WITHOUT CLICKING (owner, 2026-08-15): "the auto expanded storyboard
     text updates last too little ... not enough for them to be readable. The
     whole point should be that they last enough to be readable. So the player
     ... only clicks them if they want."

     The ladder was calibrated against the ~100-character beat the storyteller
     prompt ASKS for; the beats it actually writes run 134-316 characters, with a
     median near 208 (measured over game/ai/config/game_example_*.txt). At the
     old 9ms/char that median dwelled 2502ms — 83 characters per second, roughly
     four times faster than an adult reads narrative prose (~260 wpm ≈ 25 CPS),
     so the only way to read a beat was to hunt down its tile and click it.

     The rate is now the reading rate: ~22 CPS plus a lead-in for the eye to
     find the panel. The cap is the honest reading time of the LONGEST beat the
     storyteller produces (~293 characters), not an arbitrary truncation, and a
     click still dismisses instantly for anyone who would rather skip.
     Was 1980 / 3240 / 9 / 630. */
  const TOAST_MS = 2800;
  const MAX_STORY_REVEAL_MS = 14000;
  const STORY_READING_MS_PER_CHARACTER = 45;
  const STORY_READING_LEAD_IN_MS = 800;
  // The full chronology always remains in the durable lane. During a burst,
  // keep only the two newest waiting prose reveals behind the active one, so a
  // high-combo turn presents at most three beats instead of every one of them.
  // Since a beat now dwells long enough to READ, that ceiling is ~30s of
  // narration for a busy turn rather than the ~7s it used to be — each one a
  // single click to skip, and every dropped beat still readable from its tile.
  const MAX_QUEUED_STORY_REVEALS = 2;
  const STORY_RESERVATION_TIMEOUT_MS = 6000;
  const TURN_BARRIER_TIMEOUT_MS = 6000;
  const MANGA_IMAGE_READY_TIMEOUT_MS = 8000;
  const HOVER_LEAVE_MS = 300;
  const COMPACT_PHONE_LANE_QUERY = '(hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 560px)';
  const ZERO_SCROLLBAR_BALANCE = '0px';
  const storyArtReadiness = new WeakMap();

  function storyRevealDuration(storyText) {
    const readingTime = STORY_READING_LEAD_IN_MS
      + String(storyText || '').length * STORY_READING_MS_PER_CHARACTER;
    return Math.min(MAX_STORY_REVEAL_MS, Math.max(TOAST_MS, readingTime));
  }

  const SIDE_COLORS = { player: '#eac761', opponent: '#a06fd6' };

  /* System beats that carry a STAKE rather than narration: an amber tile, a ⚠
     glyph, and their own text as the accessible label, because there is no card
     to name. Each keeps its own class so a spec can target one of them. */
  const WARNING_KINDS = new Set(['deckout', 'timeout']);

  // ---- highlight colors (ported verbatim from UI.Story) -------------------
  const nameColorOverrides = {
    'Orion': '#6b9fff',         // blue
    'Sylva': '#6bcf6b'          // green
  };
  let dynamicNameColors = {};
  const colorPalette = [
    '#ffdf5c', '#e06c75', '#c678dd', '#56b6c2', '#98c379', '#d19a66', '#be5046', '#ab47bc'
  ];

  function getHighlightColor(name) {
    if (nameColorOverrides[name]) return nameColorOverrides[name];
    if (dynamicNameColors[name]) return dynamicNameColors[name];
    const color = colorPalette[Object.keys(dynamicNameColors).length % colorPalette.length];
    dynamicNameColors[name] = color;
    return color;
  }

  // A status arrives decorated: bold markers from the storyteller, a trailing
  // period, and a stack/turn counter that changes every tick. The colour
  // registry is keyed by the exact string, so colouring the decorated form gave
  // one effect a new colour whenever its counter moved, and made the card
  // ("Poisoned") and the summoner rail ("Poisoned.") disagree. Reduce every
  // status to its bare effect name so all three surfaces — prose, card overlay,
  // summoner rail — land on one entry.
  function statusColorKey(effect) {
    return String(effect == null ? '' : effect)
      .replace(/\*\*/g, '')
      .replace(/[.,;:!]+$/, '')
      .replace(/\s*(?:\((?:[^()]*)\)|[x×]\s*\d+)\s*$/i, '')
      .trim();
  }

  function getStatusColor(effect) {
    const key = statusColorKey(effect);
    return key ? getHighlightColor(key) : '';
  }

  function tr(key, vars, fallback) {
    try {
      if (i18n && typeof i18n.t === 'function') {
        const value = i18n.t(key, vars || {});
        if (value && value !== key) return value;
      }
    } catch (_) { /* fall through to fallback */ }
    let text = fallback;
    if (vars) {
      Object.keys(vars).forEach((k) => {
        text = text.replace('{{' + k + '}}', vars[k]);
      });
    }
    return text;
  }

  function loadStoryArtImage(image, source) {
    if (!image || !source || image.dataset.storyArtSource === source) return;
    image.dataset.storyArtSource = source;
    const cards = Cards;
    if (typeof cards?.loadCardArtImage === 'function') {
      const readiness = cards.loadCardArtImage(
        image,
        source,
        { fit: 'contain' },
        { directArtOnly: true },
      );
      if (readiness && typeof readiness.then === 'function') {
        storyArtReadiness.set(image, readiness);
      }
      return;
    }
    if (typeof window.MoonfallRetryingImage?.load === 'function') {
      window.MoonfallRetryingImage.load(image, source);
      return;
    }
    // cards.js is loaded before this module in the production document. Keep
    // the exact source visible in an isolated/module-test shell as well; never
    // substitute a logo, cardback, or placeholder image.
    image.src = source;
  }

  function disposeStoryArtImage(image) {
    if (!image) return;
    Cards?.disposeCardArtImage?.(image);
    window.MoonfallRetryingImage?.dispose?.(image);
    storyArtReadiness.delete(image);
    delete image.dataset.storyArtSource;
  }

  function disposeStoryArtWithin(root) {
    if (!root) return;
    if (root.matches?.('img')) disposeStoryArtImage(root);
    root.querySelectorAll?.('img').forEach(disposeStoryArtImage);
  }

  // Parse `**Name**` markers into colored .story-highlight spans — the same
  // XSS-safe DOM building the old rail used (text nodes only, no HTML).
  function renderStoryText(target, storyText) {
    const parts = String(storyText).split(/\*\*(.+?)\*\*/g);
    parts.forEach((part, index) => {
      if (index % 2 === 1) {
        const span = document.createElement('span');
        span.className = 'story-highlight';
        span.style.color = getHighlightColor(part.trim());
        span.textContent = part;
        target.appendChild(span);
      } else if (part) {
        target.appendChild(document.createTextNode(part));
      }
    });
  }

  // ---- state ---------------------------------------------------------------
  let entries = [];                 // ordered oldest -> newest
  // After this long a pending panel escalates its label (watchdog copy);
  // the server's own expiry remains the hard bound.
  const MANGA_SLOW_PANEL_MS = 4 * 60 * 1000;
  let mangaByTurn = new Map();      // turn -> {turn, artifactId, imageUrl, imageStatus}
  // Each authoritative beat arrives through two channels: storyHistory for
  // durable state and storyUpdate for the live reveal. Entries remember which
  // history slot confirmed them and whether their live reveal already ran, so
  // equal prose from different cards/turns remains distinct.
  // How many leading storyHistory entries the client has confirmed; the
  // server history shrinking BELOW this is the only true undo signal (a
  // count merely lower than live entries just means history lags the events).
  let historyConfirmedCount = 0;
  // Per-turn column view: which turn's column is shown, and whether the view
  // auto-advances to the newest turn (true until the player pages back with ‹).
  let viewTurn = null;
  let followLatest = true;
  let visibleEntriesByTurn = new Map();
  // A new visible beat owns one scroll correction. Ordinary rerenders (font,
  // language, art readiness, resize) must never destroy manual lane browsing.
  let pendingLatestLaneTurn = null;
  const latestLanePresentationByRecord = new WeakMap();
  let completedSummonKeys = new Set();
  let idSeq = 0;
  let translationRevision = 0;

  function nextId() {
    idSeq += 1;
    return 'sf-' + idSeq;
  }

  function ownPlayerId() {
    try {
      const state = GameState && GameState.get ? GameState.get() : null;
      return state ? state.playerId : null;
    } catch (_) {
      return null;
    }
  }

  function resolveSide(playerId) {
    if (!playerId) return 'neutral';
    return playerId === ownPlayerId() ? 'player' : 'opponent';
  }

  function summonFeedEnabled() {
    return window.__MOONFALL_CONFIG?.features?.storyFeedSummons === true;
  }

  function makeEntry(fields) {
    return Object.assign({
      id: nextId(),
      turn: null,
      side: 'neutral',
      kind: 'story',
      cardInstanceId: null,
      cardId: null,
      cardName: null,
      cardType: null,
      cardSnapshot: null,
      targetPresentation: null,
      targetPresentationOwnerOnly: false,
      actionId: null,
      actionPlayerId: null,
      resolutionId: null,
      externalKey: null,
      historyIndex: null,
      announced: false,
      renderRevision: 0,
      summonKey: null,
      summonName: null,
      storyText: null,
      pending: false,
      announcedBySpellFloat: false,
      autoRevealCancelled: false,
      createdAt: Date.now(),
    }, fields);
  }

  function requestLatestLanePosition(record) {
    if (!record || typeof record !== 'object') return;
    const turn = turnBucket(record.turn);
    const presentation = record.imageStatus
      ? `manga:${record.imageStatus}`
      : `${record.pending ? 'pending' : 'settled'}:${record.storyText ? 'story' : 'silent'}`;
    const signature = `${turn}:${presentation}`;
    if (latestLanePresentationByRecord.get(record) === signature) return;
    latestLanePresentationByRecord.set(record, signature);
    pendingLatestLaneTurn = turn;
  }

  function pushEntry(entry) {
    requestLatestLanePosition(entry);
    entries.push(entry);
    if (entries.length > ENTRY_CAP) entries.splice(0, entries.length - ENTRY_CAP);
    render();
    return entry;
  }

  function removeEntry(id) {
    const index = entries.findIndex((e) => e.id === id);
    if (index !== -1) {
      entries.splice(index, 1);
      cancelQueuedDisplay('story:' + id);
      render();
      // An empty-story resolution can expire while a completed manga or later
      // play is waiting behind its reserved FIFO slot. Removing the slot must
      // immediately wake the presenter; otherwise the remaining chronology is
      // stranded until some unrelated future socket event happens to pump it.
      pumpDisplayQueue();
      resumeDeferredMangaPanelWhenIdle();
    }
  }

  // Newest first: re-casts of the same conjured instance settle the most
  // recent pending tile.
  function normalizedResolutionId(value) {
    const raw = value?.resolutionId ?? value?.action?.resolutionId;
    if (typeof raw !== 'string') return null;
    const resolutionId = raw.trim();
    return resolutionId || null;
  }

  function normalizedActionId(value) {
    const raw = typeof value === 'string' ? value : value?.actionId;
    if (typeof raw !== 'string') return null;
    const actionId = raw.trim();
    return actionId && actionId.length <= 128 ? actionId : null;
  }

  function duplicatePendingAction(actionId, cardInstanceId) {
    if (actionId) {
      return entries.some((entry) => entry.pending && entry.actionId === actionId);
    }
    return Boolean(newestPendingFor(cardInstanceId));
  }

  function newestPendingFor(cardInstanceId, resolutionId = null) {
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      const entry = entries[i];
      if (!entry.pending || entry.cardInstanceId !== cardInstanceId) continue;
      const entryResolutionId = normalizedResolutionId(entry);
      // A pending action may adopt its first authoritative id. Once bound, it
      // can settle only from that exact resolution; an id-less legacy payload
      // must not consume an id-bearing pending action.
      if (entryResolutionId && entryResolutionId !== resolutionId) continue;
      return entry;
    }
    return null;
  }

  function entryMatchesStoryPayload(entry, payload) {
    if (!entry || entry.pending) return false;
    const entryResolutionId = normalizedResolutionId(entry);
    const payloadResolutionId = normalizedResolutionId(payload);
    if (entryResolutionId || payloadResolutionId) {
      return Boolean(
        entryResolutionId
        && payloadResolutionId
        && entryResolutionId === payloadResolutionId
      );
    }
    if (entry.storyText !== payload.story) return false;
    if (payload.cardInstanceId != null) {
      if (entry.kind !== 'story' || entry.cardInstanceId !== payload.cardInstanceId) return false;
      return typeof payload.turn !== 'number'
        || typeof entry.turn !== 'number'
        || entry.turn === payload.turn;
    }
    if (payload.playerId || typeof payload.turn === 'number') {
      if (entry.kind !== 'turnEnd') return false;
      if (
        typeof payload.turn === 'number'
        && typeof entry.turn === 'number'
        && entry.turn !== payload.turn
        && entry.turn !== payload.turn + 1
      ) return false;
      if (!payload.playerId) return true;
      const side = resolveSide(payload.playerId);
      return entry.side === 'neutral' || entry.side === side;
    }
    return entry.kind === 'prologue';
  }

  function newestSettledEntryFor(payload, predicate = () => true) {
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const entry = entries[index];
      if (entryMatchesStoryPayload(entry, payload) && predicate(entry)) return entry;
    }
    return null;
  }

  // Copy the payload's card identity onto the entry. Announcement enrichment
  // must never overwrite identity the entry already carries.
  function copyCardIdentity(entry, payload, { overwrite }) {
    ['cardId', 'cardName', 'cardType'].forEach((field) => {
      if (payload[field] && (overwrite || !entry[field])) entry[field] = payload[field];
    });
  }

  function snapshotTargetPresentation(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    if (value.kind === 'card') {
      const card = snapshotCard(value.card);
      if (!card) return null;
      const snapshot = { kind: 'card', card };
      if (typeof value.playerId === 'string' && value.playerId) {
        snapshot.playerId = value.playerId;
      }
      if (value.zone === 'hand' || value.zone === 'summoned') {
        snapshot.zone = value.zone;
      }
      return Object.freeze(snapshot);
    }
    if (value.kind === 'player' && typeof value.playerId === 'string' && value.playerId) {
      return Object.freeze({ kind: 'player', playerId: value.playerId });
    }
    if (value.kind === 'zone'
      && typeof value.playerId === 'string'
      && (value.zone === 'hand' || value.zone === 'deck')) {
      return Object.freeze({ kind: 'zone', playerId: value.playerId, zone: value.zone });
    }
    if (value.kind === 'hiddenHandCard'
      && typeof value.playerId === 'string'
      && Number.isInteger(value.handIndex)
      && value.handIndex >= 0) {
      return Object.freeze({
        kind: 'hiddenHandCard',
        playerId: value.playerId,
        handIndex: value.handIndex,
      });
    }
    return null;
  }

  function copyTargetPresentation(entry, payload) {
    const presentation = snapshotTargetPresentation(payload?.targetPresentation);
    if (!presentation) return;
    const wouldDowngradeOwnerTarget = entry.targetPresentationOwnerOnly
      && entry.targetPresentation?.kind === 'card'
      && presentation.kind === 'hiddenHandCard';
    if (!wouldDowngradeOwnerTarget) entry.targetPresentation = presentation;
  }

  function authorizedOwnerTarget(payload) {
    return payload?.targetPresentationOwnerOnly === true
      && typeof payload.playerId === 'string'
      && payload.playerId === ownPlayerId();
  }

  // Preserve only the display metadata already broadcast by the authoritative
  // action event. Spells leave the hand before their story arrives, so looking
  // them up in live zones loses their mana, rules text, and art. Keeping this
  // immutable snapshot makes a later preview identical to the card that cast.
  const CARD_SNAPSHOT_FIELDS = [
    'id', 'cardId', 'instanceId', 'name', 'type', 'description', 'manaCost',
    'picture', 'imageUrl', 'targetingType', 'status', 'rarity', 'stellar',
    'isStellar', 'stellarPicture', 'stellarVideo', 'stellarSound',
    'stellarVideoUrl', 'stellarVideoWebmUrl',
    'stellarSummonVideoUrl', 'stellarSummonVideoWebmUrl',
    'stellarCastVideoUrl', 'stellarCastVideoWebmUrl',
  ];

  function snapshotCard(card) {
    if (!card || typeof card !== 'object') return null;
    const snapshot = {};
    CARD_SNAPSHOT_FIELDS.forEach((field) => {
      if (card[field] !== undefined && card[field] !== null) snapshot[field] = card[field];
    });
    return Object.keys(snapshot).length ? Object.freeze(snapshot) : null;
  }

  // Re-render and toast a just-settled entry — unless the opponent spell float
  // already showed the card while the story was being written (a second toast
  // for the same beat would double-fire).
  function announceSettled(entry) {
    if (entry.announced) return;
    entry.announced = true;
    requestLatestLanePosition(entry);
    render();
    const guidedCardlessBeat = tutorialGuidanceActive() && !entry.cardInstanceId;
    if (entry.announcedBySpellFloat
      || guidedCardlessBeat
      || terminalDisplayState()) {
      entry.autoRevealCancelled = true;
      cancelQueuedDisplay('story:' + entry.id);
      // The cancelled reservation may have been the FIFO head. Wake the ready
      // story/manga immediately behind it instead of waiting for an unrelated
      // transient event that may never arrive.
      pumpDisplayQueue();
      resumeDeferredMangaPanelWhenIdle();
      return;
    }
    queueStoryDisplay(entry);
  }

  function createSettled(payload, fields) {
    const entry = pushEntry(makeEntry(Object.assign({
      turn: typeof payload.turn === 'number' ? payload.turn : null,
      resolutionId: normalizedResolutionId(payload),
      storyText: payload.story,
    }, fields)));
    announceSettled(entry);
  }

  // ---- event intake --------------------------------------------------------

  function onSpellCastStarted(payload) {
    if (!payload || !payload.cardInstanceId) return;
    const actionId = normalizedActionId(payload);
    if (duplicatePendingAction(actionId, payload.cardInstanceId)) return;
    const side = resolveSide(payload.playerId);
    const card = payload.card || {};
    const entry = pushEntry(makeEntry({
      turn: typeof payload.turn === 'number' ? payload.turn : null,
      side,
      kind: 'story',
      cardInstanceId: payload.cardInstanceId,
      cardId: card.id || null,
      cardName: card.name || null,
      cardType: card.type || null,
      cardSnapshot: snapshotCard(Object.assign({}, card, {
        instanceId: card.instanceId || payload.cardInstanceId,
      })),
      targetPresentation: snapshotTargetPresentation(payload.targetPresentation),
      targetPresentationOwnerOnly: authorizedOwnerTarget(payload),
      actionId,
      actionPlayerId: payload.playerId || null,
      pending: true,
      announcedBySpellFloat: side === 'opponent',
    }));
    reserveStoryDisplay(entry);
  }

  function onCharacterActionStarted(payload) {
    const cardInstanceId = payload?.characterId || payload?.creatureId;
    const actionId = normalizedActionId(payload);
    if (!cardInstanceId || duplicatePendingAction(actionId, cardInstanceId)) return;
    const card = payload.card || {};
    const entry = pushEntry(makeEntry({
      turn: typeof payload.turn === 'number' ? payload.turn : null,
      side: resolveSide(payload.playerId),
      kind: 'story',
      cardInstanceId,
      cardId: card.id || null,
      cardName: card.name || payload.characterName || payload.creatureName || null,
      cardType: card.type || 'Character',
      cardSnapshot: snapshotCard(Object.assign({}, card, {
        instanceId: card.instanceId || cardInstanceId,
      })),
      targetPresentation: snapshotTargetPresentation(payload.targetPresentation),
      targetPresentationOwnerOnly: authorizedOwnerTarget(payload),
      actionId,
      actionPlayerId: payload.playerId || null,
      pending: true,
    }));
    reserveStoryDisplay(entry);
  }

  function onGameActionSettled(payload) {
    if (typeof payload?.succeeded !== 'boolean') return;
    const actionId = normalizedActionId(payload);
    if (!actionId) return;
    const playerId = typeof payload.playerId === 'string' ? payload.playerId : null;
    const unresolvedEntryIds = entries
      .filter((entry) => (
        entry.pending
        && entry.actionId === actionId
        && (!entry.actionPlayerId || entry.actionPlayerId === playerId)
      ))
      .map((entry) => entry.id);
    unresolvedEntryIds.forEach(removeEntry);
  }

  function summonText(name) {
    return tr('storyFeed.summoned', { name }, '{{name}} answers the call.');
  }

  function onSummonCompleted(payload) {
    if (!summonFeedEnabled() || !payload?.cardInstanceId || !payload?.cardName) return;
    const turn = typeof payload.turn === 'number' ? payload.turn : null;
    const key = [turn, payload.playerId || '', payload.cardInstanceId].join(':');
    if (completedSummonKeys.has(key)) return;
    completedSummonKeys.add(key);

    pushEntry(makeEntry({
      turn,
      side: resolveSide(payload.playerId),
      kind: 'summon',
      cardInstanceId: payload.cardInstanceId,
      cardId: payload.cardId || null,
      cardName: payload.cardName,
      cardType: 'Character',
      cardSnapshot: snapshotCard({
        id: payload.cardId,
        instanceId: payload.cardInstanceId,
        name: payload.cardName,
        type: 'Character',
        picture: payload.picture,
        imageUrl: payload.imageUrl,
      }),
      storyText: summonText(payload.cardName),
      pending: false,
      summonKey: key,
      summonName: payload.cardName,
    }));
  }

  function upsertSystemEntry(storyText, options = {}) {
    const text = typeof storyText === 'string' ? storyText.trim() : '';
    const key = typeof options.key === 'string' ? options.key.trim() : '';
    if (!text || !key) return false;

    const kind = WARNING_KINDS.has(options.kind) ? options.kind : 'system';
    const side = ['player', 'opponent', 'neutral'].includes(options.side)
      ? options.side
      : 'neutral';
    const turn = Number.isInteger(options.turn) ? options.turn : null;
    const existing = entries.find((entry) => entry.externalKey === key);

    if (existing) {
      const changed = existing.storyText !== text
        || existing.kind !== kind
        || existing.side !== side
        || existing.turn !== turn;
      existing.storyText = text;
      existing.kind = kind;
      existing.side = side;
      existing.turn = turn;
      existing.pending = false;
      if (changed) existing.renderRevision += 1;
      render();
      return true;
    }

    const entry = pushEntry(makeEntry({
      externalKey: key,
      turn,
      side,
      kind,
      storyText: text,
      pending: false,
    }));
    return true;
  }

  function onStoryUpdate(payload) {
    if (!payload || !payload.story) return;

    // Exact correlation wins before pending settlement. A delayed redelivery
    // for resolution A must never consume a newly opened same-card pending
    // action for resolution B.
    const correlated = normalizedResolutionId(payload)
      ? newestSettledEntryFor(payload)
      : null;

    if (payload.cardInstanceId != null) {
      if (correlated) {
        if (typeof payload.turn === 'number' && correlated.turn == null) correlated.turn = payload.turn;
        if (!correlated.storyText) correlated.storyText = payload.story;
        correlated.cardInstanceId = correlated.cardInstanceId || payload.cardInstanceId;
        copyCardIdentity(correlated, payload, { overwrite: false });
        copyTargetPresentation(correlated, payload);
        announceSettled(correlated);
        return;
      }
      const resolutionId = normalizedResolutionId(payload);
      const pending = newestPendingFor(payload.cardInstanceId, resolutionId);
      if (pending) {
        pending.storyText = payload.story;
        pending.pending = false;
        pending.resolutionId = resolutionId;
        if (typeof payload.turn === 'number') pending.turn = payload.turn;
        copyCardIdentity(pending, payload, { overwrite: true });
        copyTargetPresentation(pending, payload);
        announceSettled(pending);
        return;
      }
      // The beat may already sit in the feed via storyHistory reconciliation
      // (the state broadcast precedes this event). storyUpdate is then the
      // ANNOUNCEMENT channel: enrich the bare history entry with the card
      // identity it lacks and fire its toast.
      const existing = newestSettledEntryFor(payload);
      if (existing) {
        if (typeof payload.turn === 'number' && existing.turn == null) existing.turn = payload.turn;
        existing.cardInstanceId = existing.cardInstanceId || payload.cardInstanceId;
        copyCardIdentity(existing, payload, { overwrite: false });
        copyTargetPresentation(existing, payload);
        announceSettled(existing);
        return;
      }
      createSettled(payload, {
        side: resolveSide(payload.playerId),
        kind: 'story',
        cardInstanceId: payload.cardInstanceId,
        cardId: payload.cardId || null,
        cardName: payload.cardName || null,
        cardType: payload.cardType || null,
        targetPresentation: snapshotTargetPresentation(payload.targetPresentation),
      });
      return;
    }

    if (payload.playerId) {
      // End-turn narration: no single card, attributed to the acting player.
      const existing = correlated || newestSettledEntryFor(payload);
      if (existing) {
        // Backfill the acting side and narrated turn from the live channel.
        if (existing.side === 'neutral') existing.side = resolveSide(payload.playerId);
        if (typeof payload.turn === 'number') existing.turn = payload.turn;
        announceSettled(existing);
        return;
      }
      createSettled(payload, { side: resolveSide(payload.playerId), kind: 'turnEnd' });
      return;
    }

    /* A story that names a TURN is that turn's narration, even when the server
       could not attribute it to a player. `broadcastTurnEndResult` publishes
       `playerId: storyContext.playerId || null`, so an unattributable end-turn
       beat used to fall through to the prologue branch and mint a second
       neutral opening beat — painting the same ✶ tile as the battle-location
       prologue, once per turn. The prologue is the ONE story with no turn:
       the opening broadcast carries `{ story }` alone. */
    if (typeof payload.turn === 'number') {
      if (newestSettledEntryFor(payload)) return;
      createSettled(payload, { side: 'neutral', kind: 'turnEnd' });
      return;
    }

    // Prologue / initial story — fires twice (initialStory + storyUpdate), so
    // exact-text dedup is load-bearing; the re-fire stays silent.
    if (newestSettledEntryFor(payload)) return;
    createSettled(payload, { side: 'neutral', kind: 'prologue' });
  }

  function onMangaGenerationStarted(payload) {
    if (!payload || payload.status !== 'pending' || typeof payload.turn !== 'number') return;
    if (terminalDisplayState()) return;
    const existing = mangaByTurn.get(payload.turn);
    // Either pending announcement may be the first one a reconnecting client
    // receives. Establish the end-turn barrier before the enrichment early
    // return so artifact-id redelivery cannot accidentally skip chronology.
    // A delayed redelivery after completion is only stale enrichment and must
    // not install a fresh, never-released barrier behind the finished turn.
    if (payload.awaitsTurnResolution === true
      && (!existing || existing.imageStatus === 'pending')) {
      reserveTurnResolutionBarrier(payload.turn);
    }
    if (existing && existing.imageStatus !== 'failed') {
      // Live admission first reserves the turn with artifactId:null, then the
      // accepted workflow event supplies its durable id. Enrich that same
      // record/tile instead of silently discarding the authoritative id.
      if (existing.imageStatus === 'pending' && !existing.artifactId && payload.artifactId) {
        existing.artifactId = payload.artifactId;
        render();
      }
      // A repeat is state enrichment/re-delivery, not a fresh announcement.
      // Re-showing it can let a delayed older turn replace the newer pending
      // panel already visible (or waiting behind a coach mark).
      return;
    }
    const record = {
      turn: payload.turn,
      artifactId: payload.artifactId || null,
      imageUrl: null,
      imageStatus: 'pending',
      pendingSince: Date.now(),
    };
    mangaByTurn.set(payload.turn, record);
    requestLatestLanePosition(record);
    render();
    updatePendingMangaStatus({ announce: true });
  }

  // Timestamp of the last manga_reveal chime, so multi-panel bursts (e.g.
  // several turns completing together) only sound once. (Ported from UI.Story.)
  let lastMangaRevealSoundAt = 0;
  const MANGA_REVEAL_SOUND_DEBOUNCE_MS = 2500;

  function mangaTileEl(turn) {
    return document.querySelector('.story-tile--manga[data-manga-turn="' + turn + '"]');
  }

  /** Celebrate a LIVE-generated panel landing: tile pop + unique chime + duck. */
  function revealMangaTile(turn) {
    const tile = mangaTileEl(turn);
    if (tile) {
      tile.classList.add('story-tile--reveal');
      setTimeout(() => tile.classList.remove('story-tile--reveal'), 900);
    }
    const now = Date.now();
    if (now - lastMangaRevealSoundAt >= MANGA_REVEAL_SOUND_DEBOUNCE_MS || lastMangaRevealSoundAt === 0) {
      lastMangaRevealSoundAt = now;
      SoundEffects?.play('manga_reveal');
      Soundtrack?.duckVolume?.(0.6, 2000);
    }
  }

  // ---- manga auto-reveal side panel ----------------------------------------
  // Pending generation is a durable status. Completed manga panels are finite
  // reveals and share the same FIFO presenter as card-backed story actions, so
  // bursts remain readable instead of replacing one another.
  const MANGA_PANEL_MS = 1800;
  const MANGA_BLOCKER_RECONCILE_MS = 0;
  let mangaPanelEl = null;
  let mangaPanelTimer = null;
  // Live completions whose picture is still being measured for their reveal.
  let mangaRevealsInFlight = 0;
  let deferredMangaTurn = null;
  let mangaBlockerRetryTimer = null;
  let mangaBlockerObserver = null;
  const mangaImageReadinessCancels = new Set();

  function clearMangaPanel() {
    if (mangaPanelTimer) { clearTimeout(mangaPanelTimer); mangaPanelTimer = null; }
    if (mangaPanelEl) {
      disposeStoryArtWithin(mangaPanelEl);
      mangaPanelEl.hidden = true;
    }
    if (!isElementOpen(popupEl) && !isElementOpen(actionStageEl) && !isElementOpen(zoomEl)) {
      hideFocusBackdrop();
    }
    if (deferredMangaTurn == null && displayQueue.length === 0) stopMangaBlockerRetry();
  }

  function stopMangaBlockerRetry() {
    if (mangaBlockerRetryTimer) {
      clearTimeout(mangaBlockerRetryTimer);
      mangaBlockerRetryTimer = null;
    }
    if (mangaBlockerObserver) {
      mangaBlockerObserver.disconnect();
      mangaBlockerObserver = null;
    }
  }

  function forgetDeferredMangaPanel() {
    deferredMangaTurn = null;
    if (displayQueue.length === 0) stopMangaBlockerRetry();
  }

  /* WHAT COUNTS AS A MODAL THAT OWNS INPUT.
     `alertdialog` was missing, and it is the role the app's OWN confirmation
     uses — correctly, since it is a question that interrupts. The scan below
     therefore never saw `Confirm`, and a story transient or a completed manga
     panel could open on top of "Are you sure you want to return to the lobby?"
     while the player was answering it. Reachable in any ordinary match: the
     tutorial coach mark blocked it by accident during the tutorial, and is not
     on screen the rest of the time.
     Contract: tests/unit/ui/storyFeed.toast.test.js. */
  const MODAL_DIALOG_SELECTOR = '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]';

  /* OPEN MEANS RENDERED, AND `display` DOES NOT INHERIT. A hub sheet left open
     inside the hidden lobby computes `display: grid` on its own in WebKit and
     Firefox, so checking only the element made it a modal that owned every
     match started from it: no reveal opened and no square answered a press.
     Any hidden ancestor closes it, the same walk chrome-menu.js makes. */
  function elementIsVisiblyOpen(element) {
    if (!element || !element.isConnected) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    try {
      const style = window.getComputedStyle(element);
      if (style.visibility === 'hidden'
        || style.visibility === 'collapse'
        || style.opacity === '0') return false;
      for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
        if (node.hidden || window.getComputedStyle(node).display === 'none') return false;
      }
      return true;
    } catch (_) {
      return !element.hidden
        && element.style.display !== 'none'
        && element.style.visibility !== 'hidden';
    }
  }

  function tutorialCoachMarkVisible() {
    return Array.from(document.querySelectorAll('.tutorial-tip')).some(elementIsVisiblyOpen);
  }

  function mangaTransientBlocked() {
    return mulliganActive() || tutorialCoachMarkVisible() || externalModalOwnsInput();
  }

  function scheduleMangaBlockerRetry() {
    const panelOpen = !!mangaPanelEl && mangaPanelEl.isConnected && !mangaPanelEl.hidden;
    if (deferredMangaTurn == null && !panelOpen && displayQueue.length === 0) {
      stopMangaBlockerRetry();
      return;
    }
    if (typeof MutationObserver === 'function' && document.body) {
      if (!mangaBlockerObserver) {
        mangaBlockerObserver = new MutationObserver(scheduleMangaBlockerRetry);
      } else {
        mangaBlockerObserver.disconnect();
      }
      // Both blocker surfaces mount directly under <body>. Watch only those
      // top-level mounts plus the blockers' own visibility attributes: the
      // animated board mutates constantly, and observing its whole subtree
      // would turn unrelated card/UI churn into reconciliation work.
      mangaBlockerObserver.observe(document.body, {
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      const blockers = [
        document.getElementById('mulligan-overlay'),
        ...document.querySelectorAll('.tutorial-tip'),
        ...document.querySelectorAll(MODAL_DIALOG_SELECTOR),
      ].filter(Boolean);
      blockers.forEach((blocker) => mangaBlockerObserver.observe(blocker, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style', 'aria-hidden'],
      }));
    }
    if (mangaBlockerRetryTimer) return;
    mangaBlockerRetryTimer = setTimeout(() => {
      mangaBlockerRetryTimer = null;
      reconcileMangaPanelBlocker();
      pumpDisplayQueue();
      const stillOpen = !!mangaPanelEl && mangaPanelEl.isConnected && !mangaPanelEl.hidden;
      if (deferredMangaTurn == null && !stillOpen && displayQueue.length === 0) {
        stopMangaBlockerRetry();
      }
    }, MANGA_BLOCKER_RECONCILE_MS);
  }

  function deferMangaPanel(record) {
    if (!record) return;
    // Image jobs may complete out of order. Never let an older panel replace a
    // later turn already waiting behind the same coach mark.
    if (deferredMangaTurn != null && record.turn < deferredMangaTurn) return;
    deferredMangaTurn = record.turn;
    if (activeDisplay?.type === 'manga' && activeDisplay.turn === record.turn) {
      const suspended = activeDisplay;
      activeDisplay = null;
      if (!displayQueue.some((item) => item.key === suspended.key)) {
        displayQueue.unshift(suspended);
      }
    }
    clearMangaPanel();
    scheduleMangaBlockerRetry();
  }

  function revealDeferredMangaPanel() {
    if (deferredMangaTurn == null) return;
    if (mangaTransientBlocked()) {
      scheduleMangaBlockerRetry();
      return;
    }
    // A deferred completed panel must never interrupt another finite reveal or
    // its timer. If it arrived while the blocker was present, start that FIFO
    // first and let finishActiveDisplay resume this completion afterward.
    if (activeDisplay || manualInspectOpen || isElementOpen(zoomEl)) return;
    const ownBarrierKey = turnResolutionBarriers.get(deferredMangaTurn);
    const hasEarlierQueuedReveal = displayQueue.some((item) => item.key !== ownBarrierKey);
    if (hasEarlierQueuedReveal) {
      pumpDisplayQueue();
      if (activeDisplay
        || displayQueue.some((item) => item.key !== ownBarrierKey)) return;
    }
    const turn = deferredMangaTurn;
    const record = mangaByTurn.get(turn);
    forgetDeferredMangaPanel();
    if (record?.imageStatus === 'complete') showMangaPanel(record);
  }

  function reconcileMangaPanelBlocker() {
    if (mangaTransientBlocked()) {
      if (!mangaPanelEl || !mangaPanelEl.isConnected || mangaPanelEl.hidden) return;
      deferMangaPanel(mangaByTurn.get(Number(mangaPanelEl.dataset.mangaTurn)));
      return;
    }
    revealDeferredMangaPanel();
  }

  function scheduleMangaPanelDismiss() {
    if (mangaPanelTimer) clearTimeout(mangaPanelTimer);
    mangaPanelTimer = setTimeout(() => {
      mangaPanelTimer = null;
      if (activeDisplay?.type === 'manga') finishActiveDisplay();
      else {
        if (mangaPanelEl) {
          disposeStoryArtWithin(mangaPanelEl);
          mangaPanelEl.hidden = true;
        }
        /* The panel is gone, so its tap-catcher must go with it — a leaked
           transparent pointer-events:auto backdrop at z-index 9540 would
           silently swallow the next tap anywhere on screen. */
        hideFocusBackdrop();
        if (deferredMangaTurn == null) stopMangaBlockerRetry();
        pumpDisplayQueue();
      }
    }, MANGA_PANEL_MS);
  }

  function clearMangaPanelDismiss() {
    if (!mangaPanelTimer) return;
    clearTimeout(mangaPanelTimer);
    mangaPanelTimer = null;
  }

  function scheduleCompletedMangaPanelDismiss() {
    if (!mangaPanelEl || mangaPanelEl.hidden) return;
    scheduleMangaPanelDismiss();
  }

  function ensureMangaPanel() {
    if (mangaPanelEl && mangaPanelEl.isConnected) return mangaPanelEl;
    mangaPanelEl = document.createElement('div');
    mangaPanelEl.id = 'story-feed-manga-panel';
    mangaPanelEl.setAttribute('role', 'button');
    mangaPanelEl.tabIndex = 0;
    mangaPanelEl.hidden = true;
    // hover pins it (desktop); leaving re-arms the auto-dismiss.
    mangaPanelEl.addEventListener('mouseenter', clearMangaPanelDismiss);
    mangaPanelEl.addEventListener('mouseleave', scheduleCompletedMangaPanelDismiss);
    /* INSPECTION highlight — never the auto-reveal.
       A panel that simply arrives is a notification and must stay quiet, the
       same way a story beat does. A panel the player is actually touching (or
       hovering) is being read, so it gets the darkened surround a card gets
       when it is inspected. Driven by a class rather than `:active` because
       iOS applies `:active` inconsistently, and the class is also what keeps
       the two states — arrived vs being read — genuinely distinguishable.
       Activation is unchanged: the click still opens the fullscreen viewer. */
    const markInspecting = () => {
      clearMangaPanelDismiss();
      mangaPanelEl.classList.add('story-feed-manga-panel--inspecting');
    };
    const clearInspecting = () => {
      mangaPanelEl.classList.remove('story-feed-manga-panel--inspecting');
    };
    mangaPanelEl.addEventListener('pointerenter', markInspecting);
    mangaPanelEl.addEventListener('pointerdown', markInspecting);
    mangaPanelEl.addEventListener('pointerup', clearInspecting);
    mangaPanelEl.addEventListener('pointerleave', clearInspecting);
    mangaPanelEl.addEventListener('pointercancel', clearInspecting);
    // Keyboard users receive the same pinning contract as mouse hover. Never
    // auto-hide the focused button and strand focus inside hidden content.
    mangaPanelEl.addEventListener('focusin', clearMangaPanelDismiss);
    mangaPanelEl.addEventListener('focusout', scheduleCompletedMangaPanelDismiss);
    mangaPanelEl.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      mangaPanelEl.click();
    });
    document.body.appendChild(mangaPanelEl);
    return mangaPanelEl;
  }

  function mangaPanelActionLabel(record) {
    return tr('storyFeed.historyOpen', null, 'Open the battle chronicle')
      + ', ' + turnLabelText(record?.turn);
  }

  function activateMangaTransient(turn) {
    // The expanded illustration is already the player's chosen target. Promote
    // that same panel into the existing accessible viewer; dismissal remains
    // available from the viewer close control, backdrop, or Escape.
    openMangaZoom(turn, mangaTileEl(turn));
  }

  function renderMangaPanel(record) {
    if (!record || !record.imageUrl || record.imageStatus !== 'complete' || terminalDisplayState()) return;
    const panel = ensureMangaPanel();
    const turn = record.turn;
    const img = document.createElement('img');
    img.className = 'story-feed-manga-panel-img';
    img.alt = '';
    const cap = document.createElement('div');
    cap.className = 'story-feed-manga-panel-cap';
    cap.textContent = turnLabelText(turn);
    disposeStoryArtWithin(panel);
    panel.replaceChildren(img, cap);
    loadStoryArtImage(img, record.imageUrl);
    panel.className = '';
    panel.dataset.mangaTurn = String(turn);
    panel.setAttribute('role', 'button');
    panel.setAttribute('aria-label', mangaPanelActionLabel(record));
    panel.removeAttribute('aria-live');
    panel.removeAttribute('aria-busy');
    panel.removeAttribute('aria-describedby');
    panel.tabIndex = 0;
    panel.hidden = false;
    panel.onclick = () => activateMangaTransient(turn);
    // restart the entrance animation on each fresh panel
    panel.classList.remove('story-feed-manga-panel--in');
    void panel.offsetWidth;
    panel.classList.add('story-feed-manga-panel--in');
    scheduleMangaPanelDismiss();
    scheduleMangaBlockerRetry();
  }

  function showMangaPanel(record) {
    if (!record || !record.imageUrl || record.imageStatus !== 'complete') return;
    if (mangaTransientBlocked()) {
      // A completed panel is a finite reveal, so retain it in the same FIFO as
      // stories before recording which manga state should wake the presenter
      // when the blocker disappears. deferredMangaTurn alone intentionally
      // keeps only the newest status and must not be the reveal history.
      queueMangaDisplay(record);
      deferMangaPanel(record);
      return;
    }
    if (deferredMangaTurn === record.turn) forgetDeferredMangaPanel();
    if (mangaPanelEl?.dataset.mangaTurn === String(record.turn)) clearMangaPanel();
    queueMangaDisplay(record);
  }

  /**
   * Await the actual pixels: "displayed" means the tile's image decoded, not
   * merely that a src was assigned — the tutorial finale confirms display to
   * the server off this promise, and the old rail honored the same contract.
   *
   * THE SQUARE CAN LEAVE MID-DECODE. A panel that completes as the player ends
   * a turn is measured on its square while the feed follows the next turn;
   * that square is unmounted and its picture disposed, and the disposed decode
   * answers "not displayable". That was the only live completion (repeats are
   * silent), so the panel never auto-expanded: the tutorial's turn-3 manga,
   * every time (owner, 27 Oct). The picture is fine; measure it once more,
   * within the time left, where an unmounted turn is always measured (off-page
   * or on a remounted square). A timeout or a reset's cancel is final.
   * @returns {Promise<boolean>}
   */
  function awaitTileImageDisplay(turn, imageUrl, {
    timeoutMs = MANGA_IMAGE_READY_TIMEOUT_MS,
    remeasured = false,
  } = {}) {
    const startedAt = Date.now();
    const tile = mangaTileEl(turn);
    let img = tile ? tile.querySelector('img.story-tile-art') : null;
    let detachedProbe = false;
    if (!imageUrl) return Promise.resolve(false);
    // Only one chronicle turn is mounted at a time. When an older queued turn
    // completes off-page, decode the same resource in a detached Image so its
    // eventual panel is pixel-ready without forcing navigation away from the
    // turn the player is currently inspecting.
    if (!img && typeof window.Image === 'function') {
      img = new window.Image();
      detachedProbe = true;
      img.hidden = true;
      img.setAttribute('aria-hidden', 'true');
      img.dataset.storyOffpageProbe = 'true';
      document.body.appendChild(img);
      loadStoryArtImage(img, imageUrl);
    }
    if (!img) return Promise.resolve(false);
    if (img.complete && img.naturalWidth > 0) return Promise.resolve(true);
    const exactReadiness = storyArtReadiness.get(img);
    if (exactReadiness) {
      return new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;
        const cancel = () => finish(false, { final: true });
        function finish(displayed, { final = false } = {}) {
          if (settled) return;
          settled = true;
          if (timeoutId !== null) clearTimeout(timeoutId);
          mangaImageReadinessCancels.delete(cancel);
          if (detachedProbe) {
            disposeStoryArtImage(img);
            img.remove();
          }
          const timeLeftMs = timeoutMs - (Date.now() - startedAt);
          if (displayed !== true && !final && !remeasured && !detachedProbe
            && !img.isConnected && timeLeftMs > 0) {
            resolve(awaitTileImageDisplay(turn, imageUrl, { timeoutMs: timeLeftMs, remeasured: true }));
            return;
          }
          resolve(displayed === true);
        }
        mangaImageReadinessCancels.add(cancel);
        timeoutId = setTimeout(() => finish(false, { final: true }), timeoutMs);
        Promise.resolve(exactReadiness).then(
          (ready) => finish(ready === true && img.dataset.storyArtSource === imageUrl),
          () => finish(false),
        );
      });
    }
    // Environments without decode() (jsdom, ancient engines) cannot observe
    // pixel readiness — report displayed rather than hanging the finale.
    if (typeof img.decode !== 'function') return Promise.resolve(true);
    return new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;

      const onLoad = () => finish(img.naturalWidth > 0);
      // The exact-source owner keeps retrying after an error. The first failed
      // transport is never authority to abandon a recorded finale.
      const onError = () => {};
      const cancel = () => finish(false);
      const cleanup = () => {
        if (timeoutId !== null) clearTimeout(timeoutId);
        timeoutId = null;
        img.removeEventListener?.('load', onLoad);
        img.removeEventListener?.('error', onError);
        mangaImageReadinessCancels.delete(cancel);
      };
      function finish(displayed) {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(displayed === true);
      }

      mangaImageReadinessCancels.add(cancel);
      timeoutId = setTimeout(() => finish(false), timeoutMs);
      img.addEventListener?.('load', onLoad);
      img.addEventListener?.('error', onError);
      // decode() may reject before a later load event succeeds, especially
      // while the browser swaps a responsive image candidate. A rejection is
      // therefore not terminal; load/error or the bounded timeout owns that.
      Promise.resolve()
        .then(() => img.decode())
        .then(() => finish(true))
        .catch(() => {
          if (img.complete && img.naturalWidth > 0) finish(true);
        });

      // Close the race where the image completed between the first readiness
      // check and listener registration.
      if (img.complete && img.naturalWidth > 0) finish(true);
    });
  }

  function cancelMangaImageReadinessWaits() {
    Array.from(mangaImageReadinessCancels).forEach((cancel) => cancel());
  }

  /**
   * @returns {Promise<boolean>} true when a completed panel was stored,
   * rendered, AND its image decoded — the tutorial finale awaits this
   * before confirming display to the server.
   */
  function onStoryImageUpdate(payload) {
    if (!payload || typeof payload.turn !== 'number') return Promise.resolve(false);
    if (payload.status === 'complete' && payload.imageUrl) {
      const prior = mangaByTurn.get(payload.turn);
      if (prior?.artifactId && payload.artifactId !== prior.artifactId) {
        return Promise.resolve(false);
      }
      // Only a LIVE pending→complete transition celebrates; hydration/rejoin
      // inserts (no pending slot) stay silent.
      const wasLivePending = !!prior && prior.imageStatus === 'pending';
      const completedRecord = prior?.imageStatus === 'complete'
        && prior.imageUrl === payload.imageUrl
        ? prior
        : {
          turn: payload.turn,
          artifactId: payload.artifactId || prior?.artifactId || null,
          imageUrl: payload.imageUrl,
          imageStatus: 'complete',
        };
      if (completedRecord !== prior) {
        mangaByTurn.set(payload.turn, completedRecord);
        render();
      }
      updatePendingMangaStatus();
      if (wasLivePending) mangaRevealsInFlight += 1;
      const settleInFlight = () => {
        if (wasLivePending) mangaRevealsInFlight = Math.max(0, mangaRevealsInFlight - 1);
      };
      return awaitTileImageDisplay(payload.turn, payload.imageUrl).then((displayed) => {
        settleInFlight();
        if (!displayed) return false;
        // Loading completion and pixel readiness are separate events. Only a
        // still-current live completion may enter the finite reveal queue;
        // reset/undo or a replacement artifact while decode was pending wins.
        if (wasLivePending
          && mangaByTurn.get(payload.turn) === completedRecord
          && !terminalDisplayState()) {
          revealMangaTile(payload.turn);
          showMangaPanel(completedRecord);
        }
        return mangaByTurn.get(payload.turn) === completedRecord;
      }, (error) => {
        settleInFlight();
        throw error;
      });
    }
    if (payload.status === 'failed') {
      const record = mangaByTurn.get(payload.turn);
      if (!record || record.imageStatus !== 'pending') return Promise.resolve(false);
      if (record.artifactId && payload.artifactId !== record.artifactId) {
        return Promise.resolve(false);
      }
      record.imageStatus = 'failed';
      render();
      updatePendingMangaStatus();
      if (deferredMangaTurn === payload.turn) forgetDeferredMangaPanel();
      if (mangaPanelEl?.dataset.mangaTurn === String(payload.turn)) {
        clearMangaPanel();
        pumpDisplayQueue();
      }
    }
    return Promise.resolve(false);
  }

  function onGameStateUpdated(_gameState) {
    if (_gameState?.state === 'ended' || _gameState?.winner) {
      dismissTransient({ flush: true, terminal: true });
      return;
    }
    // Grace sweep: a cast that reached the server but resolved with no story
    // (LOAD_TEST_MODE, empty resolutions) must not leave a spinner behind.
    const gracedIds = entries
      .filter((entry) => entry.pending && !entry.actionId)
      .map((entry) => entry.id);
    if (!gracedIds.length) return;
    setTimeout(() => {
      gracedIds.forEach((id) => {
        const entry = entries.find((e) => e.id === id);
        if (entry && entry.pending) removeEntry(id);
      });
    }, PENDING_GRACE_MS);
  }

  // ---- reconciliation (ported from board.js handleStoryHistory) ------------

  function applyHistoryCardSnapshot(entry, item) {
    const sourceCard = snapshotCard(item?.sourceCard || item?.action?.sourceCard);
    if (!sourceCard) return entry;
    entry.cardSnapshot = sourceCard;
    entry.cardInstanceId = sourceCard.instanceId || item?.action?.sourceCardId || entry.cardInstanceId;
    entry.cardId = sourceCard.id || sourceCard.cardId || entry.cardId;
    entry.cardName = sourceCard.name || entry.cardName;
    entry.cardType = sourceCard.type || entry.cardType;
    return entry;
  }

  function applyHistoryTargetPresentation(entry, item) {
    copyTargetPresentation(entry, item);
    return entry;
  }

  function entryFromHistory(item, historyIndex) {
    const action = item.action || null;
    if (!action) {
      /* The prologue is not a turn beat. Server history stamps it with the
         turn that happened to be current when it was written, while the live
         broadcast carries no turn at all — so leaving it in made the two
         representations of the SAME opening story fail to reconcile, and the
         feed kept both. Normalise to the live shape. */
      return makeEntry({
        turn: null,
        side: 'neutral',
        kind: 'prologue',
        storyText: item.story,
        historyIndex,
      });
    }
    if (action.type === 'endTurn') {
      return makeEntry({
        turn: item.turn,
        side: resolveSide(action.playerId),
        kind: 'turnEnd',
        resolutionId: normalizedResolutionId(item),
        storyText: item.story,
        historyIndex,
      });
    }
    return applyHistoryTargetPresentation(applyHistoryCardSnapshot(makeEntry({
      turn: item.turn,
      side: resolveSide(action.playerId),
      kind: 'story',
      cardInstanceId: action.sourceCardId || null,
      resolutionId: normalizedResolutionId(item),
      storyText: item.story,
      historyIndex,
    }), item), item);
  }

  function historyPayload(item) {
    const action = item?.action || null;
    return {
      story: item?.story,
      /* An actionless history item IS the prologue, and the live broadcast of
         that same story carries no turn. Server history stamps it with
         whatever turn was current when it was written, so passing that through
         made the two representations of one opening beat fail to correlate and
         the feed kept both. Mirror the live shape. */
      turn: action ? item?.turn : undefined,
      playerId: action?.playerId || null,
      resolutionId: normalizedResolutionId(item),
      cardInstanceId: action && action.type !== 'endTurn'
        ? action.sourceCardId || null
        : null,
    };
  }

  function reconcile(storyHistory) {
    if (!Array.isArray(storyHistory)) return;
    // Reconciliation replays whole histories (reconnect/undo), so per-entry
    // renders here would make hydration O(N^2) — a guaranteed long task at
    // exactly the rejoin moment on long matches. Batch: suppress render()
    // during the loops, then flush ONE pass before returning, so the DOM is
    // still synchronously up to date by the time reconcile() exits.
    renderSuppressed = true;
    try {
      reconcileEntries(storyHistory);
    } finally {
      renderSuppressed = false;
      if (renderPending) {
        renderPending = false;
        render();
      }
    }
  }

  function reconcileEntries(storyHistory) {
    // Reconciliation is context recovery (reconnect/undo), never a live beat,
    // so it appends/rebuilds silently — no toasts. Live beats can outrun the
    // history (AI turns), so counts are compared against what the history
    // itself previously confirmed, not against live entries.
    if (storyHistory.length < historyConfirmedCount) {
      // True undo: previously confirmed beats vanished. Rebuild. Manga records
      // live in a separate turn-keyed map and deliberately survive.
      entries = [];
      storyHistory.forEach((item, index) => pushEntry(entryFromHistory(item, index)));
      historyConfirmedCount = storyHistory.length;
      pruneOrphanedStoryDisplays();
      return;
    }

    // The server history is append-only (storyEngine only ever pushes;
    // shrinkage is the rebuild branch above) and every item below
    // historyConfirmedCount was settled by the pass that confirmed it, so
    // only the appended delta needs scanning — rescanning the prefix would
    // re-hash every story text in the match on EVERY state broadcast.
    // Live storyUpdate beats can outrun history within this delta window, so
    // correlate unconfirmed live entries before appending durable copies.
    for (let i = historyConfirmedCount; i < storyHistory.length; i += 1) {
      const item = storyHistory[i];
      const hasInspectableAction = Boolean(item?.action?.sourceCardId && item?.sourceCard);
      if (!item || (!item.story && !hasInspectableAction)) continue;
      const payload = historyPayload(item);
      const correlated = normalizedResolutionId(payload)
        ? newestSettledEntryFor(payload)
        : null;
      if (correlated) {
        if (correlated.historyIndex == null) correlated.historyIndex = i;
        applyHistoryCardSnapshot(correlated, item);
        applyHistoryTargetPresentation(correlated, item);
        render();
        continue;
      }
      // The state broadcast precedes the storyUpdate event, so the live
      // spellCastStarted pending tile for this cast is usually still open:
      // settle IT in place instead of appending a second entry for the beat.
      const sourceCardId = item.action ? item.action.sourceCardId : null;
      if (sourceCardId && settlePendingEntry(sourceCardId, item, i)) continue;
      const liveEntry = newestSettledEntryFor(
        payload,
        (entry) => entry.historyIndex == null,
      );
      if (liveEntry) {
        liveEntry.historyIndex = i;
        applyHistoryCardSnapshot(liveEntry, item);
        applyHistoryTargetPresentation(liveEntry, item);
        render();
        continue;
      }
      pushEntry(entryFromHistory(item, i));
    }
    historyConfirmedCount = storyHistory.length;
  }

  /** Settle the newest pending entry for sourceCardId in place; false if none. */
  function settlePendingEntry(sourceCardId, item, historyIndex) {
    const resolutionId = normalizedResolutionId(item);
    const entry = newestPendingFor(sourceCardId, resolutionId);
    if (!entry) return false;
    const hasStory = typeof item.story === 'string' && item.story.trim().length > 0;
    entry.storyText = hasStory ? item.story : null;
    entry.pending = false;
    entry.resolutionId = resolutionId;
    if (typeof item.turn === 'number' && entry.turn == null) entry.turn = item.turn;
    entry.historyIndex = historyIndex;
    applyHistoryCardSnapshot(entry, item);
    applyHistoryTargetPresentation(entry, item);
    if (!hasStory) {
      // Privacy projection can deliberately retain an inspectable card action
      // while withholding its prose. Release the live FIFO reservation without
      // marking the entry announced: if a later authorized storyUpdate carries
      // narration, it can still auto-expand exactly once.
      cancelQueuedDisplay('story:' + entry.id);
      render();
      pumpDisplayQueue();
      resumeDeferredMangaPanelWhenIdle();
      return true;
    }
    // This is not passive hydration: a live action-start reservation already
    // exists and the authoritative history broadcast arrived before its
    // storyUpdate event. Settle and announce that exact live entry so its
    // ready:false FIFO slot cannot strand every later reveal.
    announceSettled(entry);
    return true;
  }

  // ---- lane/tile renderer ----------------------------------------------------

  function pseudoCard(entry) {
    return Object.assign({}, entry.cardSnapshot || {}, {
      cardId: entry.cardId,
      id: entry.cardId,
      instanceId: entry.cardSnapshot?.instanceId || entry.cardInstanceId,
      name: entry.cardName,
      type: entry.cardType,
    });
  }

  // Snapshot-backed history is immutable evidence of the card as played. A
  // same-instance live card may since have transformed or been enriched, so it
  // must never override the captured mana, rules text, or art. Legacy entries
  // without a snapshot may still resolve from live zones for a fuller preview.
  function resolveEntryCard(entry) {
    if (entry.cardSnapshot) return pseudoCard(entry);
    const inst = entry.cardInstanceId;
    if (inst) {
      try {
        const s = (GameState && GameState.get) ? GameState.get() : null;
        if (s) {
          const zones = [
            s.player && s.player.hand,
            s.player && s.player.summonedCards,
            s.opponent && s.opponent.summonedCards,
            s.opponent && s.opponent.revealedCards,
          ];
          for (const zone of zones) {
            if (Array.isArray(zone)) {
              const found = zone.find((c) => c && c.instanceId === inst);
              if (found) return found;
            }
          }
        }
      } catch (_) { /* fall through to the pseudo card */ }
    }
    return entry.cardId ? pseudoCard(entry) : null;
  }

  // A fixed 1×1 anchor at the viewport centre: the card preview scales from it,
  // so an inspected story-square card enlarges in the CENTRE of the screen (like
  // inspecting an enemy card) rather than off the left edge by its tile.
  function centreAnchor() {
    let a = document.getElementById('story-feed-centre-anchor');
    if (!a) {
      a = document.createElement('div');
      a.id = 'story-feed-centre-anchor';
      a.style.cssText = 'position:fixed;left:50%;top:50%;width:1px;height:1px;pointer-events:none;opacity:0;';
      document.body.appendChild(a);
    }
    return a;
  }

  function tileAriaLabel(entry) {
    const name = entry.cardName
      || (WARNING_KINDS.has(entry.kind) ? entry.storyText : null)
      || (entry.kind === 'turnEnd'
        ? tr('storyFeed.turnEndLabel', null, 'Turn end')
        : tr('storyFeed.prologueLabel', null, 'Prologue'));
    return tr('storyFeed.tileAria', { name }, 'Story beat: {{name}}');
  }

  function buildStoryTile(entry) {
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'story-tile';
    tile.dataset.storyKind = entry.kind;
    if (typeof entry.turn === 'number') tile.dataset.storyTurn = String(entry.turn);
    if (WARNING_KINDS.has(entry.kind)) tile.classList.add(`story-tile--${entry.kind}`);
    tile.setAttribute('data-entry-id', entry.id);
    tile.setAttribute('aria-label', tileAriaLabel(entry));
    tile.setAttribute('aria-controls', 'story-feed-popup');
    tile.setAttribute('aria-expanded', 'false');
    if (SIDE_COLORS[entry.side]) tile.style.setProperty('--side-color', SIDE_COLORS[entry.side]);
    if (entry.pending) tile.classList.add('is-pending');

    if (entry.cardId) {
      const img = document.createElement('img');
      img.className = 'story-tile-art';
      img.alt = '';
      try {
        if (Cards && typeof Cards.resolveCardArt === 'function') {
          Cards.resolveCardArt(pseudoCard(entry), img, {
            rasterReserveScale: 1.08,
          });
        }
      } catch (_) { /* card art is cosmetic; never break the feed */ }
      tile.appendChild(img);
    } else {
      const glyph = document.createElement('span');
      glyph.className = 'story-tile-glyph';
      glyph.textContent = entry.kind === 'turnEnd' ? '☾' : (WARNING_KINDS.has(entry.kind) ? '⚠' : '✶');
      tile.appendChild(glyph);
    }

    attachTileInspect(tile, entry);
    return tile;
  }

  function buildMangaTile(record) {
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'story-tile story-tile--manga';
    const frame = document.createElement('span');
    frame.className = 'story-tile-manga-frame';
    tile.appendChild(frame);
    tile.setAttribute('data-manga-turn', String(record.turn));
    if (record.artifactId) tile.setAttribute('data-artifact-id', record.artifactId);
    if (record.imageStatus === 'pending') {
      tile.classList.add('is-pending');
      tile.disabled = true;
      tile.setAttribute('aria-busy', 'true');
      tile.setAttribute('aria-label', tr('storyFeed.mangaPending', null, 'Manga panel on the way…'));
    } else if (record.imageStatus === 'failed') {
      tile.classList.add('is-failed');
      tile.setAttribute('aria-label', tr('storyFeed.mangaFailed', null, 'This panel failed to draw'));
    } else {
      tile.setAttribute('aria-label', tr('storyFeed.tileAria', { name: tr('storyFeed.historyTitle', null, 'Battle Chronicle') }, 'Story beat: {{name}}'));
    }

    if (record.imageUrl) {
      const img = document.createElement('img');
      img.className = 'story-tile-art';
      img.alt = '';
      frame.appendChild(img);
      loadStoryArtImage(img, record.imageUrl);
      tile.addEventListener('click', () => openMangaZoom(record.turn, tile));
      if (record.imageStatus === 'complete') attachMangaTileInspect(tile, record);
    } else {
      const glyph = document.createElement('span');
      glyph.className = 'story-tile-glyph';
      glyph.textContent = '❖';
      frame.appendChild(glyph);
      if (record.imageStatus === 'failed') {
        // Sighted players must learn a panel died too — not only ARIA users.
        const status = document.createElement('span');
        status.className = 'story-tile-pending-label story-tile-failed-label';
        status.textContent = tr('storyFeed.mangaFailed', null, 'This panel failed to draw');
        frame.appendChild(status);
      }
      if (record.imageStatus === 'pending') {
        const slow = record.pendingSince
          && Date.now() - record.pendingSince >= MANGA_SLOW_PANEL_MS;
        if (!slow && record.pendingSince && !record.slowEscalateTimerId) {
          // One bounded timeout per panel (NOT a polling loop) so the label
          // escalates even when nothing else triggers a rerender.
          record.slowEscalateTimerId = setTimeout(() => {
            record.slowEscalateTimerId = null;
            render();
          }, MANGA_SLOW_PANEL_MS - (Date.now() - record.pendingSince) + 50);
        }
        const status = document.createElement('span');
        status.className = 'story-tile-pending-label';
        status.textContent = slow
          ? tr('storyFeed.mangaSlow', null, 'Still drawing… taking longer than usual')
          : tr('manga.generating', null, 'Drawing…');
        frame.appendChild(status);
      }
    }
    return tile;
  }

  function updatePendingMangaStatus({ announce = false } = {}) {
    const status = document.getElementById('story-feed-manga-status');
    if (!status) return;
    const hasPending = Array.from(mangaByTurn.values())
      .some((record) => record.imageStatus === 'pending');
    const message = hasPending
      ? tr('storyFeed.mangaPending', null, 'Manga panel on the way…')
      : '';
    if (announce && status.textContent === message) status.textContent = '';
    status.textContent = message;
  }

  // Prologue / pre-turn beats bucket at 0 so they form the earliest column.
  function turnBucket(turn) {
    return typeof turn === 'number' ? turn : 0;
  }

  // Ascending, de-duplicated turns that have any content (a beat or a manga
  // recap) — drives the ‹ / › bar and clamps viewTurn.
  function turnsWithContent() {
    const set = new Set();
    entries.forEach((e) => set.add(turnBucket(e.turn)));
    mangaByTurn.forEach((r) => set.add(turnBucket(r.turn)));
    return Array.from(set).sort((a, b) => a - b);
  }

  function entryWindowForTurn(turn) {
    const all = entries.filter((entry) => turnBucket(entry.turn) === turn);
    const requested = visibleEntriesByTurn.get(turn) || LANE_TILE_CAP;
    const visibleCount = Math.min(all.length, requested);
    return {
      all,
      visible: all.slice(-visibleCount),
      hiddenCount: Math.max(0, all.length - visibleCount),
    };
  }

  function buildEarlierEntriesButton(turn, hiddenCount) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'story-feed-more';
    button.textContent = tr(
      'storyFeed.earlierPlays',
      { count: hiddenCount },
      '+{{count}} earlier plays',
    );
    button.addEventListener('click', () => {
      const current = visibleEntriesByTurn.get(turn) || LANE_TILE_CAP;
      visibleEntriesByTurn.set(turn, current + LANE_TILE_CAP);
      render();
      const remaining = entryWindowForTurn(turn).hiddenCount;
      const nextButton = document.querySelector('.story-feed-more');
      if (remaining > 0 && nextButton) {
        nextButton.focus();
        return;
      }
      const tiles = document.querySelectorAll('#story-feed .story-feed-column .story-tile');
      tiles[tiles.length - 1]?.focus?.({ preventScroll: true });
    });
    return button;
  }

  // Tiles are REUSED across renders (keyed by entry/panel identity) so their
  // <img> nodes keep their decoded state: rebuilding per state update would
  // churn image loads (flicker on slow devices) for no reason.
  function tileKeyForEntry(entry) {
    return 'e:' + entry.id + ':' + (entry.pending ? 'p' : 's') + ':'
      + (entry.cardId || '') + ':' + (entry.turn == null ? '' : entry.turn)
      + ':r' + entry.renderRevision
      + ':l' + translationRevision;
  }

  function tileKeyForManga(record) {
    return 'm:' + record.turn + ':' + record.imageStatus + ':'
      + (record.artifactId || '') + ':' + (record.imageUrl || '')
      + ':l' + translationRevision;
  }

  function setLaneScrollbarBalance(scroll, start, end) {
    const startValue = start > 0 ? start + 'px' : ZERO_SCROLLBAR_BALANCE;
    const endValue = end > 0 ? end + 'px' : ZERO_SCROLLBAR_BALANCE;
    if (scroll.style.getPropertyValue('--story-scrollbar-balance-start') !== startValue) {
      scroll.style.setProperty('--story-scrollbar-balance-start', startValue);
    }
    if (scroll.style.getPropertyValue('--story-scrollbar-balance-end') !== endValue) {
      scroll.style.setProperty('--story-scrollbar-balance-end', endValue);
    }
  }

  /**
   * Safari 16.4–18.1 ignores `scrollbar-gutter: stable both-edges`. With the
   * macOS classic-scrollbar preference enabled, its vertical scrollbar then
   * consumes one side of this fixed-width lane and shifts every centred tile
   * by half the scrollbar width. Mirror that measured width into the opposite
   * logical padding. Browsers that implement both-edges already balance their
   * own scrollport and must not receive a second correction.
   */
  function balanceLaneScrollbar(scroll) {
    if (!scroll) return;
    const compactPhone = window.matchMedia?.(COMPACT_PHONE_LANE_QUERY)?.matches === true;
    const supportsBothEdges = window.CSS?.supports?.(
      'scrollbar-gutter',
      'stable both-edges',
    ) === true;
    if (compactPhone || supportsBothEdges) {
      setLaneScrollbarBalance(scroll, 0, 0);
      return;
    }

    const styles = window.getComputedStyle(scroll);
    const borderWidth = (Number.parseFloat(styles.borderLeftWidth) || 0)
      + (Number.parseFloat(styles.borderRightWidth) || 0);
    const measuredWidth = scroll.offsetWidth - scroll.clientWidth - borderWidth;
    const scrollbarWidth = measuredWidth > 0.5
      ? Math.round(measuredWidth * 100) / 100
      : 0;
    // Conventional RTL scrollbars occupy physical left (logical end). The
    // mirror therefore belongs on physical right (logical start), just as an
    // LTR right-side scrollbar is mirrored on physical left.
    setLaneScrollbarBalance(
      scroll,
      scrollbarWidth,
      0,
    );
  }

  function reconcileLane(scroll, specs) {
    const existing = new Map();
    Array.from(scroll.children).forEach((node) => {
      const key = node.getAttribute('data-tile-key');
      if (key) existing.set(key, node);
    });
    const nodes = specs.map(({ key, build }) => {
      const found = existing.get(key);
      if (found) {
        existing.delete(key);
        return found;
      }
      const node = build();
      node.setAttribute('data-tile-key', key);
      return node;
    });
    existing.forEach((node) => {
      disposeStoryArtWithin(node);
      node.remove();
    });
    // In-place order pass: only move nodes that are out of position. Blindly
    // re-appending every node would detach/reattach the whole lane on every
    // render, restarting CSS animations mid-flight (the is-pending spinner,
    // the manga reveal pop). A steady-state render mutates nothing; a single
    // prepend costs one insertBefore (the shift realigns later indices).
    nodes.forEach((node, i) => {
      if (scroll.children[i] !== node) scroll.insertBefore(node, scroll.children[i] || null);
    });
    balanceLaneScrollbar(scroll);
    markLaneOverflow(scroll);
  }

  function scrollLaneToLatest(scroll) {
    if (!scroll) return;
    try {
      if (typeof scroll.scrollTo === 'function') {
        scroll.scrollTo({ top: 0, behavior: 'instant' });
      } else {
        scroll.scrollTop = 0;
      }
    } catch (_) {
      scroll.scrollTop = 0;
    }
    // Engines that silently ignore an unfamiliar ScrollOptions behavior still
    // receive the synchronous newest-first position.
    if (scroll.scrollTop !== 0) scroll.scrollTop = 0;
    markLaneOverflow(scroll);
  }

  /**
   * Tell the player the lane has more in it.
   *
   * The column has always scrolled, but on a phone that is invisible: the lane
   * is ~56px wide, iOS scrollbars are overlays that only appear mid-gesture,
   * and the newest beat is at the TOP — so a full column reads as "that is all
   * of it". A fade on whichever edge has content beyond it is the cue; the
   * attribute drives it from CSS so there is no per-frame work.
   */
  function markLaneOverflow(scroll) {
    if (!scroll) return;
    const slack = scroll.scrollHeight - scroll.clientHeight;
    if (slack <= 2) {
      scroll.removeAttribute('data-overflow');
      return;
    }
    const above = scroll.scrollTop > 2;
    const below = scroll.scrollTop < slack - 2;
    scroll.setAttribute('data-overflow', above && below ? 'both' : (above ? 'top' : 'bottom'));
  }

  // reconcile() batches its loops into one render pass; when suppressed,
  // render() records the request and the batch flushes it before returning.
  let renderSuppressed = false;
  let renderPending = false;

  function storyTileSpecs(list) {
    return list.map((entry) => ({ key: tileKeyForEntry(entry), build: () => buildStoryTile(entry) }));
  }

  function turnLabelText(turn) {
    return turn >= 1
      ? tr('game.turn', null, 'Turn') + ' ' + turn
      : tr('storyFeed.prologueLabel', null, 'Prologue');
  }

  function updateTurnBar(feed, turns) {
    const bar = feed.querySelector('.story-feed-turnbar');
    const prev = feed.querySelector('.story-feed-turn-prev');
    const next = feed.querySelector('.story-feed-turn-next');
    const idx = viewTurn === null ? -1 : turns.indexOf(viewTurn);
    if (viewTurn === null) {
      delete feed.dataset.viewTurn;
      if (bar) {
        delete bar.dataset.viewTurn;
        bar.removeAttribute('aria-label');
      }
    } else {
      const viewTurnText = String(viewTurn);
      feed.dataset.viewTurn = viewTurnText;
      if (bar) {
        bar.dataset.viewTurn = viewTurnText;
        bar.setAttribute('aria-label', turnLabelText(viewTurn));
      }
    }
    if (prev) prev.disabled = idx <= 0;
    if (next) next.disabled = idx === -1 || idx >= turns.length - 1;
    /* NOTHING TO NAVIGATE, NOTHING TO DRAW.

       With a single turn of story both arrows are disabled, and a disabled
       control is 30% opacity with no backing plate — two pale ghosts sitting on
       the floorboard art in the busiest corner of the screen. Two independent
       art reviews read them as broken or unimplemented controls rather than as
       "not available yet", and at the smallest page box they are close to
       invisible. The feed already hides itself at zero turns; one turn is the
       same situation for this bar, because navigation between one item is not a
       thing a player can do. The bar holds only these two buttons, so hiding it
       costs no label and no information. */
    if (bar) bar.hidden = turns.length <= 1;
    feed.classList.toggle('is-empty', turns.length === 0);
  }

  function render() {
    if (renderSuppressed) {
      renderPending = true;
      return;
    }
    const feed = document.getElementById('story-feed');
    const col = feed ? feed.querySelector('.story-feed-column') : null;
    if (!feed || !col) return;

    const turns = turnsWithContent();
    if (!turns.length) {
      viewTurn = null;
      reconcileLane(col, []);
      updateTurnBar(feed, turns);
      return;
    }

    const latest = turns[turns.length - 1];
    if (viewTurn === null || followLatest) {
      viewTurn = latest;
    } else if (turns.indexOf(viewTurn) === -1) {
      // the viewed turn vanished (undo/reset) — snap back to following latest.
      viewTurn = latest;
      followLatest = true;
    }

    // One column: this turn's manga recap on top (if any), then this turn's
    // beats newest-first — both sides, colour-coded per tile.
    const specs = [];
    const manga = mangaByTurn.get(viewTurn);
    if (manga) specs.push({ key: tileKeyForManga(manga), build: () => buildMangaTile(manga) });
    const entryWindow = entryWindowForTurn(viewTurn);
    specs.push(...storyTileSpecs(entryWindow.visible.slice().reverse()));
    if (entryWindow.hiddenCount > 0) {
      specs.push({
        key: 'more:' + viewTurn + ':' + entryWindow.hiddenCount + ':l' + translationRevision,
        build: () => buildEarlierEntriesButton(viewTurn, entryWindow.hiddenCount),
      });
    }
    reconcileLane(col, specs);
    col.classList.toggle('has-more', entryWindow.hiddenCount > 0);
    const latestLaneTurn = pendingLatestLaneTurn;
    if (latestLaneTurn === viewTurn) {
      pendingLatestLaneTurn = null;
      scrollLaneToLatest(col);
    }

    updateTurnBar(feed, turns);
    syncOpenPopupEntry();
    reconcileOpenMangaZoom();
    reanchorManualInspect(col);
  }

  /* Gecko never dispatches mouseleave to a node removed from the DOM, and the
     hover inspect's only exits live on the hovered tile — so a render that
     rebuilt or dropped that tile left `manualInspectOpen` latched forever:
     the popup stuck on screen and displayIsBlocked stalled the story FIFO
     until the history shrank ("one story got stuck on screen ... got stuck
     when i hovered it", Firefox). If the invoker left the document, re-anchor
     the inspect to the same entry's rebuilt tile — its own listeners take
     over — or, when the entry's tile is gone entirely, end the inspect. */
  function reanchorManualInspect(col) {
    if (!manualInspectOpen) return;
    if (popupInvoker && popupInvoker.isConnected) return;
    let replacement = null;
    if (popupEntryId != null && col) {
      try {
        const safeId = String(popupEntryId).replace(/["\\]/g, '\\$&');
        replacement = col.querySelector(`.story-tile[data-entry-id="${safeId}"]`);
      } catch (_) { replacement = null; }
    }
    if (replacement) {
      popupInvoker = replacement;
      replacement.setAttribute('aria-expanded', 'true');
      return;
    }
    closeTileInspect();
  }

  // ---- turn navigation ---------------------------------------------------------

  function goToTurn(turn) {
    const turns = turnsWithContent();
    if (turns.indexOf(turn) === -1) return;
    viewTurn = turn;
    followLatest = turn === turns[turns.length - 1];
    render();
  }

  function stepTurn(delta) {
    const turns = turnsWithContent();
    const idx = viewTurn === null ? -1 : turns.indexOf(viewTurn);
    const target = idx + delta;
    if (target < 0 || target >= turns.length) return;
    goToTurn(turns[target]);
  }

  // ---- toast popup -------------------------------------------------------------

  let popupEl = null;
  let popupEntryId = null;
  let popupInvoker = null;
  let toastTimer = null;
  let leaveTimer = null;
  let displayQueue = [];
  let activeDisplay = null;
  let manualInspectOpen = false;
  let actionStageEl = null;
  let actionStageGeneration = 0;
  let focusBackdropEl = null;
  let turnResolutionBarriers = new Map();
  let displayReservationTimers = new Map();
  let completedTurnResolutions = new Set();
  let cancelledTurnAutoReveals = new Set();
  let gameEnded = false;

  function clearPopupTimers() {
    if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
    if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
  }

  function terminalDisplayState() {
    if (gameEnded) return true;
    try {
      const state = GameState?.get?.();
      if (state?.state === 'ended' || state?.winner) return true;
    } catch (_) { /* use DOM fallback */ }
    return Boolean(document.querySelector('.game-over-popup:not([hidden])'));
  }

  // Another modal owns keyboard and attention before the lower StoryFeed
  // chronology. In particular Action Intent installs its capture listener
  // after StoryFeed, so this synchronous guard is load-bearing: Escape must
  // cancel the intent request without silently flushing background stories.
  function externalModalOwnsInput() {
    /* `action-intent-active` is deliberately NOT checked here. That body class
       is held-board PRESENTATION (it dims play affordances while an intent is
       in flight); it does not own input. The intent flow holds the ACTION —
       reading the board, including expanding storyboard tiles, must keep
       working for the whole round-trip, exactly as it does while a story
       update is loading. The intent panel itself never claims aria-modal, so
       the genuine-modal scan below correctly ignores it. */
    return Array.from(document.querySelectorAll(MODAL_DIALOG_SELECTOR))
      .some((dialog) => dialog !== zoomEl && elementIsVisiblyOpen(dialog));
  }

  function spellAnnouncementOpen() {
    const overlay = document.getElementById('spell-announce-overlay');
    return Boolean(overlay?.classList.contains('visible') || overlay?.classList.contains('leaving'));
  }

  function ensureFocusBackdrop() {
    if (focusBackdropEl?.isConnected) return focusBackdropEl;
    focusBackdropEl = document.createElement('div');
    focusBackdropEl.id = 'story-feed-focus-backdrop';
    focusBackdropEl.setAttribute('aria-hidden', 'true');
    focusBackdropEl.hidden = true;
    focusBackdropEl.addEventListener('click', () => {
      if (focusBackdropEl?.dataset.dismissible !== 'true') return;
      dismissTransient({ flush: true });
    });
    document.body.appendChild(focusBackdropEl);
    return focusBackdropEl;
  }

  /* THE TOUCH TAP-AWAY. On touch a self-expanded beat (the prose toast, the
     manga panel) is dismissed by a tap anywhere outside it: "tapping the empty
     board dismisses the self-expanded beat instantly". That catcher used to be
     the transparent full-screen backdrop, and it sat over the player's hand,
     End Turn and every other battle control: for the ~7 s an opening narration
     reads, a tap on a hand card only dismissed the narration and the card
     never got it (measured on the phone seat, 25 September 2026). The tap now
     dismisses the beat AND reaches whatever it landed on, so one tap does what
     the player meant. Desktop keeps what it had. */
  let tapAwayArmed = false;

  function coarsePointer() {
    return Boolean(window.matchMedia?.('(pointer: coarse)')?.matches);
  }

  function onTapAway(event) {
    if (event.isPrimary === false) return;
    /* The beat's own surfaces answer their own taps: the toast dismisses on
       its click, the manga panel opens the chronicle. Dismissing on the press
       would hide them and hand the trailing click to whatever sits under. */
    const target = event.target;
    if ([popupEl, mangaPanelEl, zoomEl, actionStageEl].some((surface) => surface?.contains?.(target))) return;
    dismissTransient({ flush: true });
  }

  function armTapAway() {
    if (tapAwayArmed) return;
    tapAwayArmed = true;
    document.addEventListener('pointerdown', onTapAway, true);
  }

  // Every path that ends a self-expanded beat hides the focus backdrop, so the
  // tap-away is disarmed there too.
  function disarmTapAway() {
    if (!tapAwayArmed) return;
    tapAwayArmed = false;
    document.removeEventListener('pointerdown', onTapAway, true);
  }

  function showFocusBackdrop({ dimmed = true, dismissible = true, manual = false } = {}) {
    disarmTapAway();
    const backdrop = ensureFocusBackdrop();
    CardHover?.clearActiveHandHover?.();
    backdrop.dataset.dimmed = String(Boolean(dimmed));
    backdrop.dataset.dismissible = String(Boolean(dismissible));
    backdrop.dataset.manual = String(Boolean(manual));
    // Preserve the legacy class hook for transient desktop hover while the
    // data attributes keep darkness and dismissal as independent concerns.
    backdrop.classList.toggle('is-manual', !dismissible);
    backdrop.hidden = false;
  }

  function hideFocusBackdrop() {
    disarmTapAway();
    if (!focusBackdropEl) return;
    focusBackdropEl.hidden = true;
    focusBackdropEl.classList.remove('is-manual');
    delete focusBackdropEl.dataset.dimmed;
    delete focusBackdropEl.dataset.dismissible;
    delete focusBackdropEl.dataset.manual;
  }

  function ensurePopup() {
    if (popupEl && popupEl.isConnected) return popupEl;
    popupEl = document.createElement('div');
    popupEl.id = 'story-feed-popup';
    popupEl.setAttribute('role', 'status');
    popupEl.setAttribute('aria-live', 'polite');
    popupEl.setAttribute('aria-atomic', 'true');
    popupEl.hidden = true;
    popupEl.addEventListener('click', () => {
      if (activeDisplay?.type === 'story') dismissTransient({ flush: true });
    });
    popupEl.addEventListener('mouseenter', () => clearPopupTimers());
    popupEl.addEventListener('mouseleave', () => {
      clearPopupTimers();
      leaveTimer = setTimeout(() => {
        if (activeDisplay?.type === 'story') finishActiveDisplay();
        else closeTileInspect();
      }, HOVER_LEAVE_MS);
    });
    document.body.appendChild(popupEl);
    return popupEl;
  }

  function clearPopupInvoker() {
    if (popupInvoker?.isConnected) popupInvoker.setAttribute('aria-expanded', 'false');
    popupInvoker = null;
  }

  function closePopup() {
    clearPopupTimers();
    clearPopupInvoker();
    popupEntryId = null;
    if (popupEl) {
      popupEl.hidden = true;
      popupEl.removeAttribute('data-entry-id');
      popupEl.removeAttribute('data-inspecting-card');
      popupEl.removeAttribute('data-popup-pending');
      popupEl.style.removeProperty('width');
      popupEl.style.removeProperty('max-width');
      popupEl.style.removeProperty('max-height');
      popupEl.style.removeProperty('left');
      popupEl.style.removeProperty('top');
      popupEl.style.removeProperty('transform');
    }
  }

  function clearCardPreview() {
    try { Cards?.clearHoverPreview?.(); } catch (_) { /* optional */ }
  }

  function closeStoryVisuals() {
    closePopup();
    closeActionStage();
    clearCardPreview();
    if (!isElementOpen(mangaPanelEl) && !isElementOpen(zoomEl)) hideFocusBackdrop();
  }

  function displayIsBlocked() {
    const mangaOpen = !!mangaPanelEl && mangaPanelEl.isConnected && !mangaPanelEl.hidden;
    return terminalDisplayState()
      || mangaOpen
      || isElementOpen(zoomEl)
      || mangaTransientBlocked()
      || manualInspectOpen
      || spellAnnouncementOpen();
  }

  /* A manga on its way: still being drawn, or complete and being measured
     for its reveal. The tutorial's next coach mark lets it land first
     (tutorial.js), because a panel cannot open over a coach mark. */
  function mangaRevealPending() {
    return mangaRevealsInFlight > 0
      || Array.from(mangaByTurn.values()).some((record) => record.imageStatus === 'pending');
  }

  function isTransientBusy() {
    const revealQueued = displayQueue.some((item) => (
      item.skip !== true
      && (item.ready !== false || item.type === 'story')
    ));
    return Boolean(
      activeDisplay
      || revealQueued
      || manualInspectOpen
      || isElementOpen(popupEl)
      || isElementOpen(actionStageEl)
      || isElementOpen(mangaPanelEl)
      || isElementOpen(zoomEl)
    );
  }

  function displayAlreadyQueued(key) {
    return activeDisplay?.key === key || displayQueue.some((item) => item.key === key);
  }

  function cancelQueuedDisplay(key) {
    if (!key) return;
    displayQueue = displayQueue.filter((item) => item.key !== key);
    clearDisplayReservationTimer(key);
    for (const [turn, barrierKey] of turnResolutionBarriers) {
      if (barrierKey === key) turnResolutionBarriers.delete(turn);
    }
  }

  function pruneOrphanedStoryDisplays() {
    const currentEntryIds = new Set(entries.map((entry) => entry.id));
    displayQueue
      .filter((item) => item.type === 'story' && !currentEntryIds.has(item.entryId))
      .forEach((item) => clearDisplayReservationTimer(item.key));
    displayQueue = displayQueue.filter((item) => (
      item.type !== 'story' || currentEntryIds.has(item.entryId)
    ));

    const activeStoryWasRemoved = activeDisplay?.type === 'story'
      && !currentEntryIds.has(activeDisplay.entryId);
    const popupEntryWasRemoved = popupEntryId != null
      && !currentEntryIds.has(popupEntryId);
    if (activeStoryWasRemoved) activeDisplay = null;
    if (!activeStoryWasRemoved && !popupEntryWasRemoved) return;

    // Undo is authoritative context recovery. A removed beat must disappear
    // immediately rather than lingering until its old reading timer expires,
    // and its source/target presentation must leave with it.
    manualInspectOpen = false;
    closeStoryVisuals();
    hideFocusBackdrop();
    // The removed active beat may have been the only thing preventing a
    // surviving queued manga/story from presenting after authoritative undo.
    pumpDisplayQueue();
    resumeDeferredMangaPanelWhenIdle();
  }

  function resumeDeferredMangaPanelWhenIdle() {
    const ownBarrierKey = turnResolutionBarriers.get(deferredMangaTurn);
    const earlierRevealWaiting = displayQueue.some((item) => item.key !== ownBarrierKey);
    if (activeDisplay || earlierRevealWaiting || manualInspectOpen) return;
    revealDeferredMangaPanel();
  }

  function reserveStoryDisplay(entry) {
    if (!entry || entry.autoRevealCancelled || terminalDisplayState()) return;
    const key = 'story:' + entry.id;
    if (displayAlreadyQueued(key)) return;
    displayQueue.push({ key, type: 'story', entryId: entry.id, ready: false });
    scheduleDisplayReservationTimeout(key, STORY_RESERVATION_TIMEOUT_MS);
  }

  function clearDisplayReservationTimer(key) {
    const timer = displayReservationTimers.get(key);
    if (timer) clearTimeout(timer);
    displayReservationTimers.delete(key);
  }

  function clearDisplayReservationTimers() {
    displayReservationTimers.forEach((timer) => clearTimeout(timer));
    displayReservationTimers.clear();
  }

  function scheduleDisplayReservationTimeout(key, timeoutMs) {
    clearDisplayReservationTimer(key);
    const timer = setTimeout(() => {
      displayReservationTimers.delete(key);
      const item = displayQueue.find((candidate) => candidate.key === key);
      if (!item || item.ready !== false) return;
      cancelQueuedDisplay(key);
      // Keep the pending tile as durable truth. If its narration arrives late,
      // queueStoryDisplay will append a fresh ready reveal; only the stale FIFO
      // reservation is released so unrelated later plays can continue.
      pumpDisplayQueue();
      resumeDeferredMangaPanelWhenIdle();
    }, timeoutMs);
    displayReservationTimers.set(key, timer);
  }

  function reserveTurnResolutionBarrier(turn) {
    if (!Number.isInteger(turn)
      || completedTurnResolutions.has(turn)
      || turnResolutionBarriers.has(turn)
      || terminalDisplayState()) return;
    const key = 'turn-resolution:' + turn;
    if (displayAlreadyQueued(key)) return;
    displayQueue.push({ key, type: 'turnBarrier', turn, ready: false });
    turnResolutionBarriers.set(turn, key);
    scheduleDisplayReservationTimeout(key, TURN_BARRIER_TIMEOUT_MS);
  }

  function compactStoryRevealBacklog() {
    const queuedStories = displayQueue.filter((item) => (
      item.type === 'story'
      && item.ready !== false
      && item.skip !== true
    ));
    if (queuedStories.length <= MAX_QUEUED_STORY_REVEALS) return;

    const discardedKeys = new Set(
      queuedStories
        .slice(0, queuedStories.length - MAX_QUEUED_STORY_REVEALS)
        .map((item) => item.key),
    );
    displayQueue.forEach((item) => {
      if (!discardedKeys.has(item.key)) return;
      clearDisplayReservationTimer(item.key);
      const entry = entries.find((candidate) => candidate.id === item.entryId);
      if (entry) entry.autoRevealCancelled = true;
    });
    displayQueue = displayQueue.filter((item) => !discardedKeys.has(item.key));
  }

  function onTurnResolutionComplete(payload) {
    const turn = payload?.turn;
    if (!Number.isInteger(turn)) return;
    completedTurnResolutions.add(turn);
    const key = turnResolutionBarriers.get(turn);
    if (!key) return;
    const item = displayQueue.find((candidate) => candidate.key === key);
    if (item) {
      item.ready = true;
      item.skip = true;
    }
    clearDisplayReservationTimer(key);
    turnResolutionBarriers.delete(turn);
    pumpDisplayQueue();
  }

  function queueStoryDisplay(entry) {
    if (!entry || entry.pending || !entry.storyText || entry.autoRevealCancelled) return;
    if (entry.kind === 'turnEnd'
      && Number.isInteger(entry.turn)
      && cancelledTurnAutoReveals.has(entry.turn)) {
      entry.autoRevealCancelled = true;
      cancelQueuedDisplay('story:' + entry.id);
      return;
    }
    if (terminalDisplayState() || (tutorialGuidanceActive() && !entry.cardInstanceId)) {
      entry.autoRevealCancelled = true;
      cancelQueuedDisplay('story:' + entry.id);
      return;
    }
    const key = 'story:' + entry.id;
    let queued = displayQueue.find((item) => item.key === key);
    if (queued) {
      queued.ready = true;
      clearDisplayReservationTimer(key);
    } else if (entry.kind === 'turnEnd' && Number.isInteger(entry.turn)) {
      const barrierKey = turnResolutionBarriers.get(entry.turn);
      queued = displayQueue.find((item) => item.key === barrierKey);
      if (queued) {
        clearDisplayReservationTimer(barrierKey);
        queued.key = key;
        queued.type = 'story';
        queued.entryId = entry.id;
        queued.ready = true;
        delete queued.turn;
        turnResolutionBarriers.delete(entry.turn);
      }
    }
    if (!queued) displayQueue.push({ key, type: 'story', entryId: entry.id, ready: true });
    compactStoryRevealBacklog();
    if (displayIsBlocked()) scheduleMangaBlockerRetry();
    pumpDisplayQueue();
  }

  function queueMangaDisplay(record) {
    if (!record || !record.imageUrl || record.imageStatus !== 'complete') return;
    const key = 'manga:' + (record.artifactId || record.turn);
    if (displayAlreadyQueued(key)) {
      pumpDisplayQueue();
      return;
    }
    displayQueue.push({ key, type: 'manga', turn: record.turn, ready: true });
    pumpDisplayQueue();
  }

  function showStoryDisplay(item) {
    const entry = entries.find((candidate) => candidate.id === item.entryId);
    if (!entry || entry.pending || !entry.storyText || entry.autoRevealCancelled || terminalDisplayState()) return false;
    closeStoryVisuals();
    openPopup(entry, {});
    // Automatic narration is a lightweight prose toast only. Source/target
    // cards and board dimming belong exclusively to deliberate feed inspect;
    // otherwise every player drag and enemy replay looks as if the user opened
    // the full relationship viewer themselves.
    /* TOUCH: a tap anywhere outside the toast dismisses it, and still
       reaches what it landed on (see armTapAway). Never on fine pointers,
       where a click on the board mid-play is only a click on the board. */
    if (coarsePointer()) armTapAway();
    toastTimer = setTimeout(finishActiveDisplay, storyRevealDuration(entry.storyText));
    return true;
  }

  function showMangaDisplay(item) {
    const record = mangaByTurn.get(item.turn);
    if (!record || record.imageStatus !== 'complete' || !record.imageUrl) return false;
    closeStoryVisuals();
    renderMangaPanel(record);
    // Touch dismisses it the same way the toast is (armTapAway); a desktop
    // click keeps the backdrop that catches it.
    if (coarsePointer()) armTapAway();
    else showFocusBackdrop({ dimmed: true, dismissible: true });
    return true;
  }

  function pumpDisplayQueue() {
    if (activeDisplay || !displayQueue.length || displayIsBlocked()) return;
    const next = displayQueue[0];
    if (next.ready === false) return;
    displayQueue.shift();
    if (next.skip === true || next.type === 'turnBarrier') {
      pumpDisplayQueue();
      resumeDeferredMangaPanelWhenIdle();
      return;
    }
    // A completed deferred turn is already represented by this finite queue
    // item. Consume the wake-up marker as the item starts so draining the FIFO
    // cannot enqueue the same manga a second time afterward. Pending compact
    // tile markers remain intact while all finite reveals finish.
    if (next.type === 'manga'
      && next.turn === deferredMangaTurn
      && mangaByTurn.get(next.turn)?.imageStatus === 'complete') {
      forgetDeferredMangaPanel();
    }
    activeDisplay = next;
    const shown = next.type === 'manga' ? showMangaDisplay(next) : showStoryDisplay(next);
    if (!shown) {
      activeDisplay = null;
      pumpDisplayQueue();
    }
  }

  function finishActiveDisplay() {
    if (!activeDisplay) return;
    const finished = activeDisplay;
    activeDisplay = null;
    if (finished.type === 'manga') clearMangaPanel();
    else closeStoryVisuals();
    hideFocusBackdrop();
    pumpDisplayQueue();
    resumeDeferredMangaPanelWhenIdle();
  }

  function suspendActiveDisplay({ requeue = true } = {}) {
    if (!activeDisplay) return;
    const suspended = activeDisplay;
    activeDisplay = null;
    if (suspended.type === 'manga') clearMangaPanel();
    else closeStoryVisuals();
    hideFocusBackdrop();
    if (requeue && !displayQueue.some((item) => item.key === suspended.key)) {
      displayQueue.unshift(suspended);
    }
  }

  function ensureActionStage() {
    if (actionStageEl?.isConnected) return actionStageEl;
    actionStageEl = document.createElement('div');
    actionStageEl.id = 'story-feed-action-stage';
    actionStageEl.setAttribute('aria-hidden', 'true');
    actionStageEl.hidden = true;
    document.body.appendChild(actionStageEl);
    return actionStageEl;
  }

  function labelActionEndpoint(element, endpoint, detail = '') {
    if (!element) return element;
    const source = endpoint === 'source';
    const baseLabel = tr(
      source ? 'storyFeed.actionSource' : 'storyFeed.actionTarget',
      null,
      source ? 'Action source' : 'Action target',
    );
    element.setAttribute('role', 'group');
    element.setAttribute('aria-label', detail ? `${baseLabel}: ${detail}` : baseLabel);
    return element;
  }

  function disposeActionStageBindings(root = actionStageEl) {
    if (!root) return;
    const bound = root.matches?.('[data-story-feed-live-binding]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-story-feed-live-binding]') || []);
    bound.forEach((element) => element.__storyFeedDispose?.());
  }

  function closeActionStage() {
    actionStageGeneration += 1;
    if (!actionStageEl) return;
    actionStageEl.hidden = true;
    actionStageEl.setAttribute('aria-hidden', 'true');
    actionStageEl.style.removeProperty('visibility');
    disposeActionStageBindings(actionStageEl);
    actionStageEl.replaceChildren();
  }

  function buildActionCard(card, className, suffix) {
    if (!card || !Cards?.createCardElement) return null;
    const wrap = document.createElement('div');
    wrap.className = `spell-announce-card-wrap story-feed-action-card ${className}`;
    let face;
    try {
      face = Cards.createCardElement({ ...card }, `story-feed-${suffix}-${idSeq}`);
    } catch (_) {
      return null;
    }
    if (!face) return null;
    face.classList.add('spell-announce-cardface', 'card-tilt-disabled');
    face.__moonfallPresentationCard = card;
    wrap.appendChild(face);
    // Bounds are in card units, and story-feed.css sizes --card-unit to the
    // region this stage is given, so the text fits like any card's. A compact
    // phone used to force 0.82 units here: above the fitter's own 0.75 ceiling,
    // so every long description overflowed its box and lost its first and
    // last lines.
    Cards.fitCardDescription?.(face);
    return labelActionEndpoint(wrap, suffix);
  }

  function currentGameState() {
    try { return GameState?.get?.() || {}; } catch (_) { return {}; }
  }

  function targetSideForPlayerId(playerId) {
    if (typeof playerId !== 'string' || !playerId) return null;
    const state = currentGameState();
    const ownId = state.player?.id || state.playerId || ownPlayerId();
    const opponentId = state.opponent?.id || state.opponentId;
    if (playerId === ownId) return 'player';
    if (playerId === opponentId) return 'opponent';
    const resolved = resolveSide(playerId);
    return resolved === 'player' || resolved === 'opponent' ? resolved : null;
  }

  function targetSideLabel(side) {
    if (side === 'player') return tr('storyFeed.targetYou', null, 'You');
    if (side === 'opponent') return tr('storyFeed.targetOpponent', null, 'Opponent');
    return '';
  }

  function targetZoneLabel(zone) {
    if (zone === 'hand') return tr('storyFeed.targetHand', null, 'Hand');
    if (zone === 'deck') return tr('storyFeed.targetDeck', null, 'Deck');
    if (zone === 'summoned') return tr('storyFeed.targetSummoned', null, 'Summoned');
    return '';
  }

  function targetBadge(side, zone) {
    const sideLabel = targetSideLabel(side);
    const zoneLabel = targetZoneLabel(zone);
    if (!sideLabel && !zoneLabel) return null;
    const badge = document.createElement('span');
    badge.className = 'story-feed-target-badge';
    badge.textContent = [sideLabel, zoneLabel].filter(Boolean).join(' · ');
    return badge;
  }

  function decorateTarget(element, presentation, fallbackZone = null) {
    if (!element) return element;
    const side = targetSideForPlayerId(presentation?.playerId);
    const zone = presentation?.zone || fallbackZone;
    if (side) element.dataset.targetSide = side;
    if (zone) element.dataset.targetZone = zone;
    const badge = targetBadge(side, zone);
    if (badge) {
      element.appendChild(badge);
      labelActionEndpoint(element, 'target', badge.textContent);
    }
    return element;
  }

  function selectedCardbackForSide(side) {
    if (side !== 'player' && side !== 'opponent') return '';
    const deck = document.getElementById(side === 'player' ? 'player-deck' : 'opponent-deck');
    if (!deck) return '';
    const inline = deck.style?.getPropertyValue('--cardback-image') || '';
    if (inline.trim()) return inline.trim();
    try {
      return window.getComputedStyle?.(deck)?.getPropertyValue('--cardback-image')?.trim() || '';
    } catch (_) {
      return '';
    }
  }

  function selectedCardbackSlugForSide(side) {
    if (side !== 'player' && side !== 'opponent') return '';
    const deck = document.getElementById(side === 'player' ? 'player-deck' : 'opponent-deck');
    const stamped = deck?.dataset?.cardbackArtSlug;
    if (stamped) return stamped;
    try {
      const state = GameState?.get?.() || {};
      return side === 'player'
        ? (state.player?.selectedCardback || state.user?.selectedCardback || '')
        : (state.opponent?.selectedCardback || '');
    } catch (_) {
      return '';
    }
  }

  function playerTargetElement(presentation) {
    const side = targetSideForPlayerId(presentation.playerId) || 'opponent';
    const target = document.createElement('div');
    target.className = 'story-feed-player-target';
    target.dataset.targetSide = side;
    const portrait = document.getElementById(side === 'player' ? 'player-portrait' : 'opponent-portrait');
    const syncSettledPortrait = () => {
      const art = portrait?.querySelector?.('.portrait-art');
      const settled = portrait?.dataset?.portraitResolutionState === 'resolved'
        && !portrait.classList.contains('portrait-art-retrying')
        && !portrait.classList.contains('portrait-art-unavailable');
      let backgroundImage = '';
      if (settled && art) {
        backgroundImage = art.style.backgroundImage
          || (window.getComputedStyle ? window.getComputedStyle(art).backgroundImage : '');
      }
      if (backgroundImage && backgroundImage !== 'none') {
        target.style.backgroundImage = backgroundImage;
      } else target.style.removeProperty('background-image');
    };
    if (portrait) {
      target.dataset.storyFeedLiveBinding = 'portrait';
      portrait.addEventListener('moonfall:portrait-art-settled', syncSettledPortrait);
      target.__storyFeedDispose = () => {
        portrait.removeEventListener('moonfall:portrait-art-settled', syncSettledPortrait);
        delete target.__storyFeedDispose;
        delete target.dataset.storyFeedLiveBinding;
      };
      syncSettledPortrait();
    }
    const label = document.createElement('span');
    label.textContent = side === 'player'
      ? tr('storyFeed.targetYou', null, 'You')
      : tr('storyFeed.targetOpponent', null, 'Opponent');
    target.appendChild(label);
    return labelActionEndpoint(target, 'target', label.textContent);
  }

  function hiddenTargetElement(presentation) {
    const zone = presentation.kind === 'zone' ? presentation.zone : 'hand';
    const side = targetSideForPlayerId(presentation.playerId);
    const target = document.createElement('div');
    target.className = presentation.kind === 'zone'
      ? 'story-feed-zone-target'
      : 'story-feed-hidden-target';
    const back = document.createElement('div');
    back.className = 'card-back';
    back.setAttribute('aria-hidden', 'true');
    const selectedSlug = selectedCardbackSlugForSide(side);
    if (selectedSlug) {
      back.style.setProperty(
        '--cardback-image',
        selectedSlug === 'default' ? 'url("/assets/images/cardback.webp")' : 'none',
      );
      target.dataset.storyFeedLiveBinding = 'cardback';
      let disposed = false;
      Promise.resolve().then(() => {
        if (disposed || !target.isConnected) return;
        CardbackArt?.apply?.(back, selectedSlug);
      });
      target.__storyFeedDispose = () => {
        disposed = true;
        CardbackArt?.clear?.(back);
        delete target.__storyFeedDispose;
        delete target.dataset.storyFeedLiveBinding;
      };
    } else {
      const selectedCardback = selectedCardbackForSide(side);
      if (selectedCardback) back.style.setProperty('--cardback-image', selectedCardback);
    }
    target.appendChild(back);
    labelActionEndpoint(target, 'target');
    return decorateTarget(target, presentation, zone);
  }

  function targetElementFor(entry) {
    const presentation = entry.targetPresentation;
    if (!presentation) return null;
    if (presentation.kind === 'card') {
      return decorateTarget(
        buildActionCard(presentation.card, 'story-feed-action-target', 'target'),
        presentation,
      );
    }
    if (presentation.kind === 'player') return playerTargetElement(presentation);
    if (presentation.kind === 'hiddenHandCard' || presentation.kind === 'zone') {
      return hiddenTargetElement(presentation);
    }
    return null;
  }

  /* Keep the stage in layout, unpainted, until its pictures are ready.
     `hidden` maps to display:none, which gives Cards a zero-size presentation
     and makes truthful raster settlement impossible; visibility suppresses
     the first paint without removing measurable geometry. */
  function holdActionStageUntilPainted(stage, generation) {
    stage.style.visibility = 'hidden';
    stage.hidden = false;
    stage.setAttribute('aria-hidden', 'true');
    const reveal = () => {
      if (
        generation !== actionStageGeneration
        || !manualInspectOpen
        || !stage.isConnected
      ) return false;
      stage.hidden = false;
      stage.setAttribute('aria-hidden', 'false');
      stage.style.removeProperty('visibility');
      return true;
    };
    const abandon = () => {
      if (generation !== actionStageGeneration) return false;
      stage.hidden = true;
      stage.setAttribute('aria-hidden', 'true');
      stage.style.removeProperty('visibility');
      disposeActionStageBindings(stage);
      stage.replaceChildren();
      return false;
    };
    return { reveal, abandon };
  }

  function storyArtPixelsReady(img) {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve(true);
    const readiness = storyArtReadiness.get(img);
    if (readiness) return Promise.resolve(readiness).then((ready) => ready === true, () => false);
    if (typeof img.decode !== 'function') return Promise.resolve(true);
    return img.decode().then(() => true, () => false);
  }

  /* A manga panel shown from its square stands where a story square's cards
     stand and is as tall as an inspected card: the stage gives it the same
     base unit, and its own 3:2 picture sets the width (story-feed.css). */
  function showMangaRelationship(record) {
    const stage = ensureActionStage();
    const generation = ++actionStageGeneration;
    const figure = document.createElement('figure');
    figure.className = 'story-feed-action-manga';
    const img = /** @type {HTMLImageElement & { __storyFeedDispose?: () => void }} */ (
      document.createElement('img')
    );
    img.className = 'story-feed-action-manga-img';
    img.alt = '';
    img.dataset.storyFeedLiveBinding = 'manga';
    img.__storyFeedDispose = () => disposeStoryArtImage(img);
    const caption = document.createElement('figcaption');
    caption.className = 'story-feed-target-badge';
    caption.textContent = turnLabelText(record.turn);
    figure.append(img, caption);
    disposeActionStageBindings(stage);
    stage.replaceChildren(figure);
    stage.setAttribute('role', 'group');
    stage.setAttribute(
      'aria-label',
      tr('storyFeed.historyTitle', null, 'Battle Chronicle') + ', ' + turnLabelText(record.turn),
    );
    const { reveal, abandon } = holdActionStageUntilPainted(stage, generation);
    loadStoryArtImage(img, record.imageUrl);
    storyArtPixelsReady(img).then((ready) => (ready ? reveal() : abandon()), abandon);
  }

  function showActionRelationship(entry, { requirePair = false } = {}) {
    const source = buildActionCard(resolveEntryCard(entry), 'story-feed-action-source', 'source');
    const target = targetElementFor(entry);
    if ((!source && !target) || (requirePair && (!source || !target))) {
      closeActionStage();
      return;
    }
    const stage = ensureActionStage();
    const generation = ++actionStageGeneration;
    const children = [];
    if (source) children.push(source);
    if (source && target) {
      const arrow = document.createElement('div');
      arrow.className = 'story-feed-action-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '➜';
      children.push(arrow);
    }
    if (target) children.push(target);
    disposeActionStageBindings(stage);
    stage.replaceChildren(...children);
    stage.setAttribute('role', 'group');
    stage.setAttribute(
      'aria-label',
      tr('storyFeed.actionRelationship', null, 'Action source and target'),
    );
    const { reveal, abandon } = holdActionStageUntilPainted(stage, generation);
    const settlePresentation = Cards?.settleCardRasterForPresentation;
    const settlements = Array.from(stage.querySelectorAll('.card')).map((face) => (
      typeof settlePresentation === 'function'
        ? settlePresentation(face, face.__moonfallPresentationCard, {
          oversample: 1,
          stableGeometry: true,
        })
        : true
    ));
    const hasAsyncSettlement = settlements.some((result) => (
      result && typeof result.then === 'function'
    ));
    if (!hasAsyncSettlement) {
      return settlements.every((result) => result === true) ? reveal() : abandon();
    }
    Promise.all(settlements.map((result) => Promise.resolve(result))).then(
      (results) => {
        if (results.every((result) => result === true)) reveal();
        else abandon();
      },
      abandon,
    );
    return true;
  }

  // Kill-log inspect: HOVER is transient on desktop; TAP/CLICK/keyboard
  // activation deliberately latches the relationship until dismissal. The
  // opened relationship includes the source card, target, and narration.
  function cardGestureActive() {
    return document.body?.classList?.contains('card-dragging')
      || document.body?.classList?.contains('drop-settling')
      || CardDrag?.isDragging?.() === true;
  }

  function openTileInspect(entry, tile, { sticky = false } = {}) {
    if (!entry || cardGestureActive() || terminalDisplayState() || externalModalOwnsInput()) return;
    if (activeDisplay) {
      suspendActiveDisplay({ requeue: activeDisplay.entryId !== entry.id });
    }
    manualInspectOpen = true;
    clearPopupTimers();
    openPopup(entry, {}); // no autoCollapse — hover lifecycle or explicit dismissal owns it
    if (popupEl && !popupEl.hidden) popupEl.setAttribute('data-inspecting-card', 'true');
    showActionRelationship(entry);
    showFocusBackdrop({ dimmed: true, dismissible: sticky, manual: true });
    const relationshipOpen = actionStageEl && !actionStageEl.hidden;
    if ((popupEntryId === entry.id && popupEl && !popupEl.hidden) || relationshipOpen) {
      popupInvoker = tile;
      tile?.setAttribute('aria-expanded', 'true');
    }
  }

  /* A card played by dragging still plays out after the player lets go: a
     cast winds up, slams and dissolves while card-drag.js keeps
     `card-dragging` on the body, then the board settles (`drop-settling`),
     about 1.3s in all. openTileInspect refuses both, so a release can never
     masquerade as an inspection. A KEY press on a tile cannot be a release,
     so in that window it is kept and honoured once the release has ended,
     instead of being dropped without a word. A press anywhere, the tile
     leaving the page, a new drag or a feed reset lets it go. */
  const DEFERRED_TILE_INSPECT_MAX_MS = 3000;
  const DEFERRED_TILE_INSPECT_POLL_MS = 50;
  let deferredTileInspect = null;

  function cardReleaseSettling() {
    const classes = document.body?.classList;
    return !!classes && (classes.contains('card-dragging') || classes.contains('drop-settling'))
      && CardDrag?.isDragging?.() !== true;
  }

  function cancelDeferredTileInspect() {
    const pending = deferredTileInspect;
    if (!pending) return;
    deferredTileInspect = null;
    clearTimeout(pending.timer);
    document.removeEventListener('pointerdown', pending.letGo, true);
  }

  function openTileInspectAfterRelease(entry, tile) {
    cancelDeferredTileInspect();
    const pending = { tile, timer: null, letGo: cancelDeferredTileInspect, startedAt: Date.now() };
    const check = () => {
      if (deferredTileInspect !== pending) return;
      if (!tile.isConnected || CardDrag?.isDragging?.() === true
          || Date.now() - pending.startedAt > DEFERRED_TILE_INSPECT_MAX_MS) {
        cancelDeferredTileInspect();
        return;
      }
      if (cardReleaseSettling()) {
        pending.timer = setTimeout(check, DEFERRED_TILE_INSPECT_POLL_MS);
        return;
      }
      cancelDeferredTileInspect();
      openTileInspect(entry, tile, { sticky: true });
    };
    deferredTileInspect = pending;
    document.addEventListener('pointerdown', pending.letGo, true);
    pending.timer = setTimeout(check, DEFERRED_TILE_INSPECT_POLL_MS);
  }

  function closeTileInspect() {
    manualInspectOpen = false;
    closeStoryVisuals();
    hideFocusBackdrop();
    pumpDisplayQueue();
    resumeDeferredMangaPanelWhenIdle();
  }

  function dismissTransient({ flush = true, terminal = false } = {}) {
    if (terminal) gameEnded = true;
    if (flush) {
      displayQueue.forEach((item) => {
        if (item.type !== 'story') return;
        const entry = entries.find((candidate) => candidate.id === item.entryId);
        if (entry) entry.autoRevealCancelled = true;
      });
      if (activeDisplay?.type === 'story') {
        const entry = entries.find((candidate) => candidate.id === activeDisplay.entryId);
        if (entry) entry.autoRevealCancelled = true;
      }
      turnResolutionBarriers.forEach((_key, turn) => cancelledTurnAutoReveals.add(turn));
      displayQueue = [];
      clearDisplayReservationTimers();
      turnResolutionBarriers.clear();
      deferredMangaTurn = null;
    }
    activeDisplay = null;
    manualInspectOpen = false;
    closeStoryVisuals();
    clearMangaPanel();
    hideFocusBackdrop();
    stopMangaBlockerRetry();
    if (terminal) closeMangaZoom({ restoreFocus: false });
  }

  // Desktop hover remains transient. TouchPreview's tap-on-release and native
  // click/keyboard activation are sticky manual inspection; TouchPreview
  // swallows the compatibility click trailing its tap so one gesture cannot
  // immediately double-open/close the surface.
  function mobileUiActive() {
    try { return !!(Platform && Platform.isMobileUi && Platform.isMobileUi()); }
    catch (_) { return false; }
  }

  function openMangaInspect(record, tile) {
    if (
      record?.imageStatus !== 'complete' || !record.imageUrl
      || cardGestureActive() || terminalDisplayState() || externalModalOwnsInput()
      || isElementOpen(zoomEl)
    ) return;
    if (activeDisplay) {
      suspendActiveDisplay({
        requeue: !(activeDisplay.type === 'manga' && activeDisplay.turn === record.turn),
      });
    }
    manualInspectOpen = true;
    closePopup();
    clearMangaPanel();
    showMangaRelationship(record);
    showFocusBackdrop({ dimmed: true, dismissible: false, manual: true });
    popupInvoker = tile;
    tile.setAttribute('aria-expanded', 'true');
  }

  /* A manga square answers a hovering pointer the way a story square does
     (owner, 27 Oct: hovering it "should show it"); its click still opens the
     chronicle viewer. Touch keeps tap-to-open: there is no hover to preview. */
  function attachMangaTileInspect(tile, record) {
    tile.addEventListener('mouseenter', () => { if (!mobileUiActive()) openMangaInspect(record, tile); });
    tile.addEventListener('mouseleave', () => {
      if (!mobileUiActive() && popupInvoker === tile) closeTileInspect();
    });
    tile.addEventListener('blur', () => { if (popupInvoker === tile) closeTileInspect(); });
  }

  function attachTileInspect(tile, entry) {
    tile.addEventListener('mouseenter', () => { if (!mobileUiActive()) openTileInspect(entry, tile); });
    tile.addEventListener('mouseleave', () => { if (!mobileUiActive()) closeTileInspect(); });
    const toggleTileInspect = () => {
      const inspectOpenForTile = popupInvoker === tile
        && ((popupEntryId === entry.id && popupEl && !popupEl.hidden)
          || (actionStageEl && !actionStageEl.hidden));
      if (inspectOpenForTile) {
        dismissTransient({ flush: true });
      } else {
        openTileInspect(entry, tile, { sticky: true });
      }
    };
    tile.addEventListener('click', (event) => {
      event.preventDefault();
      toggleTileInspect();
    });
    tile.addEventListener('blur', () => {
      if (deferredTileInspect?.tile === tile) cancelDeferredTileInspect();
      if (popupInvoker === tile) closeTileInspect();
    });
    // Tabbing away closes the inspect, without waiting for `blur`.
    //
    // `blur` alone was the whole mechanism, and it is not reliable: in Firefox
    // the story tile keeps focus through a Tab press — not prevented by us, no
    // inert ancestor, the document focused, five visible tabbables with the
    // tile third — so the handler above never ran and the popup stayed open on
    // a surface the player had already left. Chrome moves focus and fires it.
    //
    // `keydown` is the intent, and it is the same in every engine. Focus does
    // not need to have moved yet for the player to have said "I am leaving".
    /* ENTER AND SPACE ARE HANDLED HERE, not left to implicit button activation.
       Measured in the iPhone profile: keydown reaches the focused <button>
       story tile and WebKit dispatches NO click for it — a probe counted
       `clicks: 0` while `keydowns: ["Enter"]`. So keyboard and assistive
       activation of narration did nothing on the engine every iPhone runs,
       while Chromium and Firefox synthesized the click and looked fine.
       preventDefault() suppresses that implicit click where it does exist, so
       this remains exactly one activation in every engine. */
    tile.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        if (popupInvoker === tile) closeTileInspect();
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      if (popupInvoker !== tile && cardReleaseSettling()) {
        openTileInspectAfterRelease(entry, tile);
        return;
      }
      toggleTileInspect();
    });
    try {
      if (TouchPreview && TouchPreview.attach) {
        TouchPreview.attach(tile, {
          onPreview: () => openTileInspect(entry, tile),
          onDismiss: () => closeTileInspect(),
        });
      }
    } catch (_) { /* touch preview optional */ }
  }

  function mulliganActive() {
    try {
      if (Mulligan?.isActive) return !!Mulligan.isActive();
    } catch (_) { /* fall through to the DOM check */ }
    const overlay = document.getElementById('mulligan-overlay');
    return !!overlay && overlay.classList.contains('active');
  }

  // Tutorial guidance owns the reading surface for the whole recorded flow —
  // an auto-toast can race the next coach mark even between visible tips.
  function tutorialGuidanceActive() {
    // The organic phase deliberately keeps the tutorial session and Skip
    // control alive at step 999. Once its completion coach mark disappears it
    // is normal gameplay, so action reveals must resume despite isTutorial.
    if (tutorialCoachMarkVisible()) return true;
    try {
      if (Tutorial?.isActive?.()) {
        return Number(Tutorial.getStep?.()) !== 999;
      }
    } catch (_) { /* fall through to authoritative game state */ }
    try {
      if (GameState?.get?.()?.isTutorial === true) return true;
    } catch (_) { /* fall through to the DOM check */ }
    return !!document.querySelector('.tutorial-tip');
  }

  /* Just the story text — the narrative is the payload. No card-name title, no
     turn label, no full stat-card (which read as tiny text on a black box). The
     tile already shows which card produced the beat.

     A beat that has not been written yet says so, in the storyteller's own
     words, so a press always answers. `data-popup-pending` records which of the
     two the popup is currently showing, so `syncOpenPopupEntry()` can swap the
     prose in when it lands without rebuilding an unchanged body on every
     render. */
  function renderPopupBody(popup, entry) {
    const text = document.createElement('div');
    text.className = 'story-feed-popup-text';
    if (entry.pending) {
      text.classList.add('is-pending');
      text.textContent = tr('storyFeed.pending', null, 'The storyteller is writing…');
    } else {
      renderStoryText(text, entry.storyText);
    }
    popup.replaceChildren(text);
    popup.dataset.popupPending = entry.pending ? 'true' : 'false';
  }

  /* THE BEAT CAN ARRIVE WHILE THE PLAYER IS READING THE WAIT.
     A press on an unwritten beat opens the pending line; when the storyteller
     fills that same entry it must become the prose in place, rather than leave
     the player looking at "still writing" over a beat that has landed. Called
     from render(), which already runs on every settle. */
  function syncOpenPopupEntry() {
    if (!popupEl || popupEl.hidden || popupEntryId === null) return;
    const entry = entries.find((candidate) => candidate.id === popupEntryId);
    if (!entry) return;
    const showing = popupEl.dataset.popupPending === 'true';
    const wanted = Boolean(entry.pending);
    if (showing === wanted) return;
    // A settle that carries no prose (privacy projection) has nothing to swap
    // in; leave the pending line rather than blanking the surface.
    if (!wanted && !entry.storyText) return;
    renderPopupBody(popupEl, entry);
  }

  /* AN UNWRITTEN BEAT IS SILENT TO THE BOARD AND ANSWERS A PRESS.
     `autoCollapse` is the automatic toast: it must never interrupt play with a
     beat that has no prose yet. A PRESS is the opposite — it is the player
     asking, and returning here made the tile a focusable <button> that did
     nothing for the whole storyteller round-trip, which is every cast in every
     match. Contract: tests/unit/ui/storyFeed.toast.test.js. */
  function openPopup(entry, { autoCollapse = false } = {}) {
    if (!entry) return;
    if (entry.pending ? autoCollapse : !entry.storyText) return;
    // Don't fire the transient toast during mulligan or tutorial guidance — it
    // would overlap instructional copy. The beat still lands in the feed and
    // remains re-viewable through hover, tap, or keyboard activation.
    if (autoCollapse && (mulliganActive() || tutorialGuidanceActive())) return;
    const popup = ensurePopup();
    clearPopupTimers();
    clearPopupInvoker();
    popup.style.removeProperty('width');
    popup.style.removeProperty('max-width');
    popup.style.removeProperty('max-height');
    popup.style.removeProperty('left');
    popup.style.removeProperty('top');
    popup.style.removeProperty('transform');
    popupEntryId = entry.id;
    popup.dataset.entryId = entry.id;
    popup.setAttribute('data-side', entry.side);

    renderPopupBody(popup, entry);
    popup.hidden = false;

    if (autoCollapse) {
      toastTimer = setTimeout(() => closePopup(), storyRevealDuration(entry.storyText));
    }
  }

  function isElementOpen(el) {
    return !!el && el.isConnected && !el.hidden && el.style.display !== 'none';
  }

  // ---- manga zoom pager (ported fullscreen browser, feed edition) ----------------

  let zoomEl = null;
  let zoomTurns = [];
  let zoomIndex = -1;
  let zoomInvoker = null;
  let zoomBackgroundState = null;
  let zoomBackgroundObserver = null;

  function completedPanels() {
    return Array.from(mangaByTurn.values())
      .filter((r) => r.imageStatus === 'complete' && r.imageUrl)
      .sort((a, b) => a.turn - b.turn);
  }

  function focusWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return;
    try {
      element.focus({ preventScroll: true });
    } catch (_) {
      element.focus();
    }
  }

  // A modal must remove the game beneath it from both keyboard navigation and
  // the accessibility tree. Preserve every sibling's exact prior state: other
  // overlays may already own inert/aria-hidden, and closing this one must never
  // accidentally expose them.
  function isolateZoomSibling(element) {
    if (!zoomBackgroundState || element === zoomEl || element.nodeType !== 1) return;
    if (zoomBackgroundState.some((state) => state.element === element)) return;
    zoomBackgroundState.push({
      element,
      inert: element.inert === true,
      ariaHidden: element.getAttribute('aria-hidden'),
    });
    element.inert = true;
    element.setAttribute('aria-hidden', 'true');
  }

  function isolateZoomBackground() {
    if (!document.body || zoomBackgroundState) return;
    zoomBackgroundState = [];
    Array.from(document.body.children).forEach(isolateZoomSibling);
    if (typeof MutationObserver !== 'function') return;
    zoomBackgroundObserver = new MutationObserver((records) => {
      // A route teardown can remove the dialog without calling close. Release
      // our ownership instead of trapping subsequently mounted app content.
      if (!zoomEl?.isConnected) {
        restoreZoomBackground();
        return;
      }
      records.forEach((record) => {
        Array.from(record.addedNodes).forEach(isolateZoomSibling);
      });
    });
    zoomBackgroundObserver.observe(document.body, { childList: true });
  }

  function restoreZoomBackground() {
    if (!zoomBackgroundState) return;
    if (zoomBackgroundObserver) {
      zoomBackgroundObserver.disconnect();
      zoomBackgroundObserver = null;
    }
    zoomBackgroundState.forEach(({ element, inert, ariaHidden }) => {
      if (!element.isConnected) return;
      element.inert = inert;
      if (ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', ariaHidden);
    });
    zoomBackgroundState = null;
  }

  function closeMangaZoom({ restoreFocus = true } = {}) {
    // Nothing open? Then there is nothing to close, and — crucially — no focus
    // to restore. Without this the function ran its whole restoration path
    // regardless, fell through to `invoker = getElementById('story-feed')` and
    // focused the battle log.
    //
    // That mattered because this is a DEFENSIVE call: the orientation guard
    // invokes it on every rotation into portrait, zoomed or not. So rotating a
    // phone with nothing zoomed moved focus to the battle log, and rotating
    // back restored focus THERE instead of where the player actually was — a
    // keyboard or screen-reader user lost their place by turning the device.
    //
    // Contract: tests/unit/ui/storyFeed.closeZoomNoop.test.js
    if (!zoomEl || zoomEl.hidden) return;

    // Never reopen onto someone else's zoom state.
    const zoomedImage = zoomEl?.querySelector?.('.story-overlay-image');
    if (zoomedImage) ImageZoom?.reset?.(zoomedImage);
    const activeInsideZoom = !!zoomEl && zoomEl.contains(document.activeElement);
    const currentTurn = zoomTurns[zoomIndex]?.turn;
    if (zoomEl) {
      disposeStoryArtWithin(zoomEl);
      zoomEl.hidden = true;
    }
    restoreZoomBackground();

    let invoker = zoomInvoker?.isConnected ? zoomInvoker : null;
    if (!invoker && currentTurn != null) {
      const currentTile = mangaTileEl(currentTurn);
      if (currentTile?.querySelector('img.story-tile-art')) invoker = currentTile;
    }
    if (!invoker) invoker = document.getElementById('story-feed');
    zoomInvoker = null;
    if (restoreFocus && invoker?.isConnected) {
      if (!invoker.hasAttribute('tabindex') && invoker.id === 'story-feed') {
        invoker.tabIndex = -1;
      }
      focusWithoutScroll(invoker);
    } else if (activeInsideZoom && document.activeElement?.blur) {
      // Reset/logout may remove the invoking tile immediately after close. Do
      // not leave focus stranded on a control inside the now-hidden dialog.
      document.activeElement.blur();
    }

    zoomTurns = [];
    zoomIndex = -1;
    pumpDisplayQueue();
  }

  function renderZoomPanel() {
    // Moving to another panel starts that panel at fit, not at wherever the
    // previous one was left pinched and panned to.
    const previousImage = zoomEl?.querySelector?.('.story-overlay-image');
    if (previousImage) ImageZoom?.reset?.(previousImage);
    const record = zoomTurns[zoomIndex];
    if (!record || !zoomEl) return;
    const img = zoomEl.querySelector('img');
    const counter = zoomEl.querySelector('.story-zoom-counter');
    if (img) {
      loadStoryArtImage(img, record.imageUrl);
      img.alt = tr('storyFeed.historyTitle', null, 'Battle Chronicle')
        + ' — ' + turnLabelText(record.turn);
    }
    if (counter) {
      counter.textContent = tr('game.turn', null, 'Turn') + ' ' + record.turn
        + ' — ' + (zoomIndex + 1) + '/' + zoomTurns.length;
    }
    const prev = zoomEl.querySelector('.story-overlay-prev');
    const next = zoomEl.querySelector('.story-overlay-next');
    if (prev) prev.disabled = zoomIndex <= 0;
    if (next) next.disabled = zoomIndex >= zoomTurns.length - 1;
  }

  function reconcileOpenMangaZoom() {
    if (!isElementOpen(zoomEl)) return;
    const currentTurn = zoomTurns[zoomIndex]?.turn;
    const previousIndex = zoomIndex;
    const panels = completedPanels();
    if (!panels.length) {
      closeMangaZoom();
      return;
    }
    zoomTurns = panels;
    const matchingIndex = panels.findIndex((record) => record.turn === currentTurn);
    zoomIndex = matchingIndex >= 0
      ? matchingIndex
      : Math.min(Math.max(previousIndex, 0), panels.length - 1);
    if (!zoomInvoker?.isConnected) {
      const currentTile = mangaTileEl(zoomTurns[zoomIndex]?.turn);
      zoomInvoker = currentTile?.querySelector('img.story-tile-art') ? currentTile : null;
    }
    renderZoomPanel();
  }

  function stepZoom(delta) {
    const target = zoomIndex + delta;
    if (target < 0 || target >= zoomTurns.length) return;
    zoomIndex = target;
    renderZoomPanel();
  }

  function zoomNavButton(className, glyph, delta, ariaLabel) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.textContent = glyph;
    if (ariaLabel) btn.setAttribute('aria-label', ariaLabel);
    btn.addEventListener('click', (event) => {
      event.stopPropagation();
      stepZoom(delta);
    });
    return btn;
  }

  function zoomFocusableElements() {
    if (!zoomEl) return [];
    return Array.from(zoomEl.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), '
      + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden);
  }

  function trapZoomFocus(event) {
    const focusable = zoomFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      zoomEl.tabIndex = -1;
      focusWithoutScroll(zoomEl);
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    let target = null;
    if (!zoomEl.contains(active)) target = first;
    else if (!focusable.includes(active)) target = event.shiftKey ? last : first;
    else if (event.shiftKey && active === first) target = last;
    else if (!event.shiftKey && active === last) target = first;
    if (!target) return;
    event.preventDefault();
    focusWithoutScroll(target);
  }

  function openMangaZoom(turn, invokingElement) {
    // The portrait rotate prompt owns the entire application. Programmatic
    // clicks can still reach inert DOM nodes, so enforce the same modal order
    // in code and never let the manga dialog hide/inert the rotate prompt.
    try {
      if (OrientationGuard?.isActive?.()) return;
    } catch (_) { /* continue when the optional guard cannot answer */ }
    if (externalModalOwnsInput()) return;

    /* Decide whether this CAN open before dismantling what is on screen.
       The order used to be the other way round: suspend the active display,
       close the story visuals, and only then discover that the requested turn
       is not among the completed panels and return. The tap therefore closed
       the manga the player was looking at and opened nothing — reported as
       "tapping generated mangas on the side dismisses them rather than
       maximise on full screen". Nothing is torn down on a tap that cannot
       lead anywhere. */
    const requestedTurns = completedPanels();
    const requestedIndex = requestedTurns.findIndex((r) => r.turn === turn);
    if (requestedIndex === -1) return;

    // A deliberate zoom interaction takes precedence over automatic reveals.
    // Pause, do not discard, the active queue item and resume it after close.
    const replacesActiveManga = activeDisplay?.type === 'manga' && activeDisplay.turn === turn;
    suspendActiveDisplay({ requeue: !replacesActiveManga });
    manualInspectOpen = false;
    closeStoryVisuals();

    zoomTurns = requestedTurns;
    zoomIndex = requestedIndex;

    // If external navigation removed an open dialog, release the modal state
    // before rebuilding it. This keeps hot reloads and view teardowns safe.
    if (zoomEl && !zoomEl.isConnected) {
      restoreZoomBackground();
      zoomEl = null;
    }
    zoomInvoker = invokingElement && invokingElement.isConnected
      ? invokingElement
      : mangaTileEl(turn);

    if (!zoomEl || !zoomEl.isConnected) {
      zoomEl = document.createElement('div');
      zoomEl.id = 'story-image-overlay';
      zoomEl.className = 'story-overlay';
      zoomEl.setAttribute('role', 'dialog');
      zoomEl.setAttribute('aria-modal', 'true');
      zoomEl.setAttribute('aria-label', tr('storyFeed.historyTitle', null, 'Battle Chronicle'));

      const img = document.createElement('img');
      img.className = 'story-overlay-image';
      img.alt = '';
      zoomEl.appendChild(img);
      /* Zoom the PANEL, not the page. The browser's own pinch scales a fixed
         overlay's chrome along with the art and leaves the player panning a
         viewport instead of a picture — "full screen mangas view should allow
         zooming". Pan and double-tap come with it; a tap at rest scale still
         falls through to the backdrop, so closing is unchanged. */
      ImageZoom?.attach?.(img);

      zoomEl.appendChild(zoomNavButton('story-overlay-prev', '‹', -1, tr('manga.nav.previous', null, 'Previous panel')));
      zoomEl.appendChild(zoomNavButton('story-overlay-next', '›', 1, tr('manga.nav.next', null, 'Next panel')));

      // Visible, touch-reachable close control. Without it the only exits were
      // Escape (no keyboard on touch) and a backdrop tap (unreachable when the
      // panel fills the viewport) — leaving mobile players stuck. Mirrors the
      // storybook viewer's overlay (public/scripts/storybook/viewer.js).
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'story-overlay-close';
      closeBtn.setAttribute('aria-label', tr('manga.nav.close', null, 'Close'));
      closeBtn.textContent = '×';
      closeBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        closeMangaZoom();
      });
      zoomEl.appendChild(closeBtn);

      const counter = document.createElement('div');
      counter.id = 'story-image-overlay-description';
      counter.className = 'story-zoom-counter story-overlay-meta';
      counter.setAttribute('aria-live', 'polite');
      counter.setAttribute('aria-atomic', 'true');
      zoomEl.appendChild(counter);
      zoomEl.setAttribute('aria-describedby', counter.id);

      zoomEl.addEventListener('click', (event) => {
        if (event.target === zoomEl) closeMangaZoom();
      });
      document.body.appendChild(zoomEl);
    }

    zoomEl.hidden = false;
    renderZoomPanel();
    // Move focus before hiding the invoker's background subtree; browsers
    // correctly reject aria-hidden on an ancestor of the active element.
    focusWithoutScroll(zoomEl.querySelector('.story-overlay-close'));
    isolateZoomBackground();
  }

  // ---- global key handling (layered Escape: zoom > popup) -----------------------

  function onKeydown(event) {
    if (isElementOpen(zoomEl)) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMangaZoom();
        return;
      }
      if (event.key === 'Tab') {
        trapZoomFocus(event);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepZoom(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepZoom(1);
      }
      return;
    }
    if (event.key === 'Escape' && externalModalOwnsInput()) return;
    if (event.key === 'Escape'
      && (isElementOpen(popupEl) || isElementOpen(mangaPanelEl) || isElementOpen(actionStageEl))) {
      event.preventDefault();
      dismissTransient({ flush: true });
    }
  }

  // ---- lifecycle -----------------------------------------------------------

  function handleLanguageChange() {
    translationRevision += 1;
    entries.forEach((entry) => {
      if (entry.kind === 'summon' && entry.summonName) {
        entry.storyText = summonText(entry.summonName);
      }
    });
    render();
    updatePendingMangaStatus();

    const feed = document.getElementById('story-feed');
    feed?.setAttribute('aria-label', tr('storyFeed.playerLane', null, 'Battle log'));
    feed?.querySelector('.story-feed-turn-prev')?.setAttribute(
      'aria-label',
      tr('storyFeed.turnPrev', null, 'Previous turn'),
    );
    feed?.querySelector('.story-feed-turn-next')?.setAttribute(
      'aria-label',
      tr('storyFeed.turnNext', null, 'Next turn'),
    );
    actionStageEl?.setAttribute(
      'aria-label',
      tr('storyFeed.actionRelationship', null, 'Action source and target'),
    );

    const panelTurn = Number(mangaPanelEl?.dataset.mangaTurn);
    const panelCaption = mangaPanelEl?.querySelector('.story-feed-manga-panel-cap');
    if (panelCaption && Number.isFinite(panelTurn)) panelCaption.textContent = turnLabelText(panelTurn);
    if (mangaPanelEl && Number.isFinite(panelTurn)) {
      const record = mangaByTurn.get(panelTurn);
      if (record) mangaPanelEl.setAttribute('aria-label', mangaPanelActionLabel(record));
    }

    if (zoomEl) {
      zoomEl.setAttribute('aria-label', tr('storyFeed.historyTitle', null, 'Battle Chronicle'));
      zoomEl.querySelector('.story-overlay-prev')?.setAttribute(
        'aria-label',
        tr('manga.nav.previous', null, 'Previous panel'),
      );
      zoomEl.querySelector('.story-overlay-next')?.setAttribute(
        'aria-label',
        tr('manga.nav.next', null, 'Next panel'),
      );
      zoomEl.querySelector('.story-overlay-close')?.setAttribute(
        'aria-label',
        tr('manga.nav.close', null, 'Close'),
      );
      renderZoomPanel();
    }
  }

  function reset() {
    cancelDeferredTileInspect();
    cancelMangaImageReadinessWaits();
    entries = [];
    mangaByTurn = new Map();
    historyConfirmedCount = 0;
    dynamicNameColors = {};
    viewTurn = null;
    followLatest = true;
    visibleEntriesByTurn = new Map();
    pendingLatestLaneTurn = null;
    completedSummonKeys = new Set();
    displayQueue = [];
    clearDisplayReservationTimers();
    activeDisplay = null;
    turnResolutionBarriers = new Map();
    completedTurnResolutions = new Set();
    cancelledTurnAutoReveals = new Set();
    gameEnded = false;
    manualInspectOpen = false;
    closePopup();
    closeMangaZoom({ restoreFocus: false });
    forgetDeferredMangaPanel();
    clearMangaPanel();
    closeActionStage();
    hideFocusBackdrop();
    render();
    updatePendingMangaStatus();
  }

  function ensureFeedContainer() {
    let feed = document.getElementById('story-feed');
    if (feed) return feed;
    feed = document.createElement('div');
    feed.id = 'story-feed';
    feed.className = 'story-feed';
    feed.setAttribute('role', 'log');
    feed.setAttribute('aria-live', 'polite');
    feed.setAttribute('aria-label', tr('storyFeed.playerLane', null, 'Battle log'));

    const bar = document.createElement('div');
    bar.className = 'story-feed-turnbar';
    bar.setAttribute('role', 'group');
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'story-feed-turn-prev';
    prev.setAttribute('aria-label', tr('storyFeed.turnPrev', null, 'Previous turn'));
    prev.textContent = '‹';
    prev.addEventListener('click', () => stepTurn(-1));
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'story-feed-turn-next';
    next.setAttribute('aria-label', tr('storyFeed.turnNext', null, 'Next turn'));
    next.textContent = '›';
    next.addEventListener('click', () => stepTurn(1));
    bar.appendChild(prev);
    bar.appendChild(next);

    const column = document.createElement('div');
    column.className = 'story-feed-column';
    column.addEventListener('scroll', () => markLaneOverflow(column), { passive: true });

    const mangaStatus = document.createElement('span');
    mangaStatus.id = 'story-feed-manga-status';
    mangaStatus.className = 'story-feed-sr-only';
    mangaStatus.setAttribute('role', 'status');
    mangaStatus.setAttribute('aria-live', 'polite');
    mangaStatus.setAttribute('aria-atomic', 'true');

    feed.appendChild(bar);
    feed.appendChild(column);
    feed.appendChild(mangaStatus);
    document.body.appendChild(feed);
    return feed;
  }

  function observeLaneScrollbarBalance() {
    const column = document.querySelector('#story-feed .story-feed-column');
    if (!column) return;

    window.__storyFeedScrollbarBalanceObserver?.disconnect?.();
    window.__storyFeedScrollbarBalanceObserver = null;
    if (typeof window.ResizeObserver === 'function') {
      const observer = new window.ResizeObserver(() => balanceLaneScrollbar(column));
      observer.observe(column);
      window.__storyFeedScrollbarBalanceObserver = observer;
    }

    if (window.__storyFeedScrollbarBalanceHandler) {
      window.removeEventListener('resize', window.__storyFeedScrollbarBalanceHandler);
      window.removeEventListener('orientationchange', window.__storyFeedScrollbarBalanceHandler);
    }
    window.__storyFeedScrollbarBalanceHandler = () => {
      balanceLaneScrollbar(document.querySelector('#story-feed .story-feed-column'));
    };
    window.addEventListener('resize', window.__storyFeedScrollbarBalanceHandler);
    window.addEventListener('orientationchange', window.__storyFeedScrollbarBalanceHandler);
    balanceLaneScrollbar(column);
  }

  function init() {
    ensureFeedContainer();
    observeLaneScrollbarBalance();
    document.removeEventListener('keydown', onKeydown, true);
    document.addEventListener('keydown', onKeydown, true);
    if (window.__storyFeedLanguageHandler) {
      window.removeEventListener('moonfall:languagechange', window.__storyFeedLanguageHandler);
    }
    window.__storyFeedLanguageHandler = handleLanguageChange;
    window.addEventListener('moonfall:languagechange', handleLanguageChange);
    if (window.__storyFeedTransientHandler) {
      window.removeEventListener('moonfall:transient-change', window.__storyFeedTransientHandler);
    }
    window.__storyFeedTransientHandler = () => {
      reconcileMangaPanelBlocker();
      pumpDisplayQueue();
    };
    window.addEventListener('moonfall:transient-change', window.__storyFeedTransientHandler);
    render();
  }

  // ---- exports --------------------------------------------------------------

  StoryFeed = {
    init,
    onSpellCastStarted,
    onCharacterActionStarted,
    onGameActionSettled,
    onSummonCompleted,
    onStoryUpdate,
    upsertSystemEntry,
    onMangaGenerationStarted,
    onTurnResolutionComplete,
    onStoryImageUpdate,
    onGameStateUpdated,
    reconcile,
    reset,
    getEntries: () => entries.slice(),
    getMangaRecords: () => Array.from(mangaByTurn.values()).sort((a, b) => a.turn - b.turn),
    dismissTransient,
    isTransientBusy,
    mangaRevealPending,
    dismissMangaZoom: closeMangaZoom,
    getHighlightColor,
    getStatusColor,
    getAutomaticRevealDuration: storyRevealDuration,
  };

  // Self-initialize: the script tag sits at the end of <body>, so the DOM is
  // ready; init() is idempotent for explicit callers (tests, hot reloads).
  if (document.body) init();
})();

export { StoryFeed };
